import { useState, useEffect, useRef, useCallback } from 'react';
import {
  MessageSquare, Send, Lock, Users, Loader2, RefreshCw,
  Hash, Crown, Shield, TrendingUp, AlertTriangle, Plus, X,
  Flame, Rocket, ThumbsUp, Gem, Smile, Trash2, Ban,
  VolumeX, UserCog, MoreHorizontal, AlertCircle, ImagePlus, BookOpen, Bell, ShieldAlert, UserX, Reply, AtSign, CheckCircle, BarChart3, DollarSign, UserPlus, UserMinus,
} from 'lucide-react';
import { getChatRooms, getChatMessages, sendChatMessage, createChatChannel, moderateChat, assignChatRole, getChatRolesList, chatHeartbeat, getChatOnlineUsers, reactToMessage, getChatReactions, getChatImage, markChatRead, getChatAlerts, acknowledgeChatAlert, getGuidelinesStatus, acknowledgeGuidelines, getBannedUsers, requestUnban, getChatNotifications, markNotificationsRead, createPoll, votePoll, resolvePoll, createPrediction, votePrediction, getRoomPolls, deletePollOrPrediction, getRoomSummary, getOnboardingStatus, completeOnboarding, generateRoomContent, createTip, chatTyping, getTopCallsThisWeek, followUser, unfollowUser, checkFollowing } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { usePaidChannelStatus, useMyPaidApplications, MonetizeChannelModal, SubscribeChannelModal, PaidChannelLockedPanel } from '../components/PaidChannelUI';
import { TradeSnapshotCard, ShareTradePickerModal } from '../components/TradeSnapshot';
import { VoiceRecorderButton, VoiceNotePlayer } from '../components/VoiceNote';

// ─── Constants ───────────────────────────────────────────────────────────────

const TIER_BADGE = {
  trial:   { bg: 'bg-gray-500/15', text: 'text-gray-400' },
  starter: { bg: 'bg-blue-500/15', text: 'text-blue-400' },
  trader:  { bg: 'bg-cyan-500/15', text: 'text-cyan-400' },
  pro:     { bg: 'bg-violet-500/15', text: 'text-violet-400' },
  elite:   { bg: 'bg-amber-500/15', text: 'text-amber-400' },
};

const CHAT_ROLE_BADGE = {
  owner:      { bg: 'bg-red-500/20', text: 'text-red-400', label: 'OWNER', icon: Crown, border: 'ring-1 ring-red-500/40' },
  superadmin: { bg: 'bg-orange-500/20', text: 'text-orange-400', label: 'S.ADMIN', icon: Shield, border: 'ring-1 ring-orange-500/40' },
  admin:      { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'ADMIN', icon: Shield, border: 'ring-1 ring-amber-500/30' },
  mod:        { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'MOD', icon: Shield, border: '' },
  vip:        { bg: 'bg-violet-500/20', text: 'text-violet-400', label: 'VIP', icon: Gem, border: '' },
};

const CATEGORY_LABELS = {
  official:  { label: 'Official', color: 'text-accent-primary' },
  networks:  { label: 'DEX Networks', color: 'text-blue-400' },
  exchanges: { label: 'CEX Exchanges', color: 'text-orange-400' },
  trading:   { label: 'Trading', color: 'text-emerald-400' },
  exclusive: { label: 'Exclusive', color: 'text-amber-400' },
  community: { label: 'Community', color: 'text-violet-400' },
};

// ─── Unique color avatar from username ────────────────────────────────────────

const AVATAR_COLORS = [
  { bg: 'bg-rose-500/25', text: 'text-rose-300' },
  { bg: 'bg-pink-500/25', text: 'text-pink-300' },
  { bg: 'bg-fuchsia-500/25', text: 'text-fuchsia-300' },
  { bg: 'bg-purple-500/25', text: 'text-purple-300' },
  { bg: 'bg-violet-500/25', text: 'text-violet-300' },
  { bg: 'bg-indigo-500/25', text: 'text-indigo-300' },
  { bg: 'bg-blue-500/25', text: 'text-blue-300' },
  { bg: 'bg-sky-500/25', text: 'text-sky-300' },
  { bg: 'bg-cyan-500/25', text: 'text-cyan-300' },
  { bg: 'bg-teal-500/25', text: 'text-teal-300' },
  { bg: 'bg-emerald-500/25', text: 'text-emerald-300' },
  { bg: 'bg-green-500/25', text: 'text-green-300' },
  { bg: 'bg-lime-500/25', text: 'text-lime-300' },
  { bg: 'bg-yellow-500/25', text: 'text-yellow-300' },
  { bg: 'bg-amber-500/25', text: 'text-amber-300' },
  { bg: 'bg-orange-500/25', text: 'text-orange-300' },
];

function getAvatarColor(name) {
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function UserAvatar({ name, chatRole, avatar, size = 'w-9 h-9', textSize = 'text-sm' }) {
  if (avatar) {
    return <img src={avatar} alt={name} className={`${size} rounded-xl object-cover flex-shrink-0`} />;
  }

  const crb = CHAT_ROLE_BADGE[chatRole];
  const color = getAvatarColor(name);
  const avatarBg = crb ? `${crb.bg} ${crb.text} ${crb.border}` : `${color.bg} ${color.text}`;

  return (
    <div className={`${size} rounded-xl flex items-center justify-center ${textSize} font-bold flex-shrink-0 ${avatarBg}`}>
      {(name || '?')[0].toUpperCase()}
    </div>
  );
}

const REACTIONS = [
  // Standard
  { emoji: '👍', key: 'like', label: 'Like', cat: 'standard' },
  { emoji: '🔥', key: 'fire', label: 'Fire', cat: 'standard' },
  { emoji: '🚀', key: 'rocket', label: 'Rocket', cat: 'standard' },
  { emoji: '😂', key: 'laugh', label: 'Funny', cat: 'standard' },
  // Trading
  { emoji: '📈', key: 'bullish', label: 'Bullish', cat: 'trading', tip: 'I agree this is going up' },
  { emoji: '📉', key: 'bearish', label: 'Bearish', cat: 'trading', tip: 'I agree this is going down' },
  { emoji: '🎯', key: 'good_call', label: 'Good Call', cat: 'trading', tip: 'Excellent analysis' },
  { emoji: '💎', key: 'diamond', label: 'Diamond Hands', cat: 'trading', tip: 'Holding strong' },
  { emoji: '🧻', key: 'paper', label: 'Paper Hands', cat: 'trading', tip: 'Sold too early' },
  { emoji: '🐳', key: 'whale', label: 'Whale Move', cat: 'trading', tip: 'Big money play' },
  // Quality
  { emoji: '🧠', key: 'smart', label: 'Smart Take', cat: 'quality', tip: 'Insightful analysis' },
  { emoji: '🎓', key: 'educational', label: 'Educational', cat: 'quality', tip: 'I learned something' },
  { emoji: '💰', key: 'money', label: 'Money', cat: 'quality' },
  // Warning
  { emoji: '🚩', key: 'rugged', label: 'Rug Warning', cat: 'warning', tip: 'Looks risky — watch out' },
  { emoji: '🗑️', key: 'fud', label: 'FUD', cat: 'warning', tip: 'Fear-mongering' },
  { emoji: '🧂', key: 'salty', label: 'Salty', cat: 'warning', tip: 'Mad you missed it' },
  // Elite-only
  { emoji: '✨', key: 'verified_bullish', label: 'Verified Bullish', cat: 'elite', tip: 'Elite-verified bull signal', elite: true },
  { emoji: '🐋', key: 'whale_alert', label: 'Whale Alert', cat: 'elite', tip: 'Elite whale activity alert', elite: true },
];

const REACTION_CATS = {
  standard: { label: 'Standard', color: 'text-gray-400' },
  trading: { label: 'Trading', color: 'text-emerald-400' },
  quality: { label: 'Quality', color: 'text-blue-400' },
  warning: { label: 'Warning', color: 'text-red-400' },
  elite: { label: 'Elite', color: 'text-amber-400' },
};

// Emoji picker categories for chat input
const EMOJI_CATEGORIES = {
  'Market': ['🚀', '💎', '🐂', '🐻', '🐳', '📈', '📉', '💰', '🤑', '💸', '🏦', '⛏️', '🔗', '🪙', '💹'],
  'Sentiment': ['🔥', '❤️', '👍', '👎', '👀', '🎯', '💯', '⚡', '🙏', '🤝', '✅', '❌', '⚠️', '🚨', '💪'],
  'Fun': ['😂', '🤣', '😎', '🤔', '😱', '🥳', '😤', '🫡', '💀', '🤡', '😈', '👑', '🏆', '🎉', '🍿'],
};

const CHANNEL_ICONS = ['💬', '🎯', '💡', '🔥', '💎', '🏆', '📈', '🐂', '🐻', '🎮', '🤖', '🌍', '⚡', '🧠', '🎵'];

// Lazy-loading chat image component
function ChatImage({ imageId, roomId }) {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!imageId) return;
    getChatImage(imageId, roomId)
      .then(d => { if (d?.image) setSrc(d.image); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [imageId, roomId]);

  if (loading) return <div className="w-48 h-32 rounded-xl bg-dark-700 animate-pulse mt-1.5" />;
  if (!src) return null;

  return (
    <>
      <img src={src} alt="Shared image" onClick={() => setExpanded(true)}
        className="mt-1.5 max-w-xs max-h-64 rounded-xl border border-dark-700/50 cursor-pointer hover:brightness-110 transition-all object-cover" />
      {expanded && (
        <>
          <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center cursor-pointer" onClick={() => setExpanded(false)}>
            <img src={src} alt="Shared image" className="max-w-[90vw] max-h-[90vh] rounded-2xl shadow-2xl object-contain" />
          </div>
        </>
      )}
    </>
  );
}

function timeAgo(ts) {
  const diff = (Date.now() - new Date(ts).getTime()) / 1000;
  if (diff < 60) return 'now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

// ─── Room List Sidebar ───────────────────────────────────────────────────────

function RoomList({ rooms, activeRoom, onSelect, onCreateChannel, show, onClose, onLockedRoom }) {
  // Group rooms by category
  const grouped = {};
  for (const room of rooms) {
    const cat = room.category || 'other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(room);
  }

  const categoryOrder = ['official', 'networks', 'exchanges', 'trading', 'exclusive', 'community'];

  return (
    <>
      {show && <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={onClose} />}
      <aside className={`
        fixed lg:relative z-50 lg:z-auto top-0 left-0 h-full w-72 lg:w-64
        bg-dark-800 border-r border-dark-700/50 flex flex-col flex-shrink-0
        transition-transform duration-200 lg:translate-x-0
        ${show ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="p-4 border-b border-dark-700/50 flex items-center justify-between">
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-accent-primary" /> Community
          </h3>
          <button onClick={onCreateChannel} title="Create Channel"
            className="p-1.5 rounded-lg text-gray-500 hover:text-accent-primary hover:bg-dark-700 transition-all">
            <Plus className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto py-2 px-2">
          {categoryOrder.map(cat => {
            const catRooms = grouped[cat];
            if (!catRooms?.length) return null;
            const catInfo = CATEGORY_LABELS[cat] || { label: cat, color: 'text-gray-500' };

            return (
              <div key={cat} className="mb-3">
                <p className={`text-[9px] font-bold uppercase tracking-widest px-3 py-1 ${catInfo.color}`}>
                  {catInfo.label}
                </p>
                <div className="space-y-0.5">
                  {catRooms.map(room => {
                    const isActive = activeRoom === room.id;
                    return (
                      <button key={room.id}
                        onClick={() => {
                          if (room.accessible || room.isPaid) { onSelect(room.id); onClose(); }
                          else if (onLockedRoom) { onClose(); onLockedRoom(room); }
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-left text-sm transition-all ${
                          !room.accessible && !room.isPaid ? 'opacity-50 hover:opacity-70 hover:bg-gradient-to-r hover:from-amber-500/5 hover:to-orange-500/5 cursor-pointer' :
                          isActive ? 'bg-accent-primary/15 text-accent-primary font-medium' : 'text-gray-400 hover:bg-dark-700 hover:text-white'
                        }`}>
                        <span className="text-base flex-shrink-0">{room.icon}</span>
                        <span className="truncate flex-1">{room.name}</span>
                        {room.userCount > 0 && !isActive && room.accessible && (
                          <span className="text-[9px] text-gray-600 flex-shrink-0">{room.userCount}</span>
                        )}
                        {room.unread > 0 && !isActive && room.accessible && (
                          <span className="min-w-[18px] h-[18px] rounded-full bg-accent-primary text-dark-900 text-[10px] font-bold flex items-center justify-center flex-shrink-0 px-1">
                            {room.unread > 99 ? '99+' : room.unread}
                          </span>
                        )}
                        {room.isPaid && (
                          <span className="text-[8px] font-bold px-1.5 py-px rounded bg-amber-500/15 text-amber-400 uppercase flex items-center gap-0.5 flex-shrink-0">
                            <Crown className="w-2.5 h-2.5" /> PREMIUM
                          </span>
                        )}
                        {!room.accessible && !room.isPaid && (
                          <span className="flex items-center gap-1 flex-shrink-0">
                            {room.userCount > 0 && (
                              <span className="text-[8px] text-emerald-400 flex items-center gap-0.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                                Active
                              </span>
                            )}
                            <span className="text-[8px] font-bold px-1.5 py-px rounded bg-amber-500/15 text-amber-400 uppercase">{room.requiredTier}</span>
                            <Lock className="w-3 h-3 text-amber-500/50" />
                          </span>
                        )}
                        {room.isCustom && room.accessible && !room.isPaid && <span className="text-[8px] text-gray-600 bg-dark-600 px-1 rounded">user</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="p-3 border-t border-dark-700/50">
          <button onClick={onCreateChannel}
            className="w-full py-2 rounded-xl text-xs font-medium text-gray-500 hover:text-accent-primary hover:bg-dark-700 transition-all flex items-center justify-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> Create Channel
          </button>
        </div>
      </aside>
    </>
  );
}

// ─── Create Channel Modal ────────────────────────────────────────────────────

// ─── Community Guidelines Modal ─────────────────────────────────────────────

const GUIDELINES = [
  { icon: '🚫', title: 'No Advertising or Shilling', desc: 'Do not promote tokens, projects, or services in chat channels. Your message will be deleted and you will receive a warning. Second offense = permanent ban.', severity: 'critical' },
  { icon: '💰', title: 'No Selling or Solicitation', desc: 'No DM selling, paid group promotions, or "guaranteed returns" offers. Use the Marketing tools if you want to promote — chat is for discussion only.', severity: 'critical' },
  { icon: '🔗', title: 'No Suspicious Links', desc: 'Do not share phishing links, airdrop scam URLs, or wallet drainer contracts. Instant ban, no warnings.', severity: 'critical' },
  { icon: '🤝', title: 'Be Respectful', desc: 'No hate speech, harassment, doxxing, or personal attacks. Healthy debate about markets is encouraged — toxicity is not.', severity: 'warning' },
  { icon: '📊', title: 'No Financial Advice', desc: 'Share analysis and opinions, but do not present them as guaranteed financial advice. Always include "NFA" or "DYOR" disclaimers.', severity: 'warning' },
  { icon: '🔁', title: 'No Spam or Flooding', desc: 'Avoid repetitive messages, copypastas, or flooding channels. Keep discussions meaningful and on-topic.', severity: 'warning' },
  { icon: '🎭', title: 'No Impersonation', desc: 'Do not impersonate team members, moderators, influencers, or other users. Verified badges indicate legitimate accounts.', severity: 'warning' },
  { icon: '📢', title: 'Use the Right Channels', desc: 'Post in the appropriate channel — signals go in Signals, degen plays in Degen Plays. Off-topic posts may be deleted.', severity: 'info' },
  { icon: '🖼️', title: 'Appropriate Content Only', desc: 'Images must be related to trading, charts, or platform discussion. No NSFW, gore, or offensive imagery.', severity: 'info' },
  { icon: '⚖️', title: 'Moderation is Final', desc: 'Moderator decisions are final. If you believe an action was unjust, contact support — do not argue in chat.', severity: 'info' },
];

const PENALTIES = [
  { offense: '1st Offense', action: 'Warning + message deleted', icon: '⚠️', color: 'text-amber-400 bg-amber-500/10 border-amber-500/20' },
  { offense: '2nd Offense', action: 'Muted for 24 hours', icon: '🔇', color: 'text-orange-400 bg-orange-500/10 border-orange-500/20' },
  { offense: '3rd Offense', action: 'Permanent ban', icon: '🚫', color: 'text-red-400 bg-red-500/10 border-red-500/20' },
  { offense: 'Scam/Phishing', action: 'Instant permanent ban', icon: '💀', color: 'text-red-400 bg-red-500/10 border-red-500/20' },
];

const PRO_TIPS = [
  { icon: '📈', title: 'Trading Calls', desc: 'Type "BUY $ETH target $3000" or "SHORT $SOL" — haythix auto-detects your call, captures the entry price, and tracks your accuracy over time. Build your reputation as a Verified Caller!', example: 'BUY $ETH target $3000' },
  { icon: '🎯', title: 'Verified Caller Badge', desc: 'Reach 20+ resolved calls with 60%+ accuracy and 5%+ avg return to earn the Verified Caller badge. Your calls get tracked at 1h, 24h, and 7d checkpoints.' },
  { icon: '@', title: 'Mention Users', desc: 'Type @ followed by a username to mention someone. They\'ll get a notification. Great for calling out good analysis or asking questions.' },
  { icon: '↩️', title: 'Reply to Messages', desc: 'Hover any message and click the reply button to respond directly. The original message context appears above your reply.' },
  { icon: '🔥', title: 'Daily Streaks', desc: 'Stay active daily to build your streak. Send messages, react, or just visit the platform. Hit milestones at 3, 7, 14, 30, 60, 100, 180, and 365 days for badges!' },
  { icon: '😎', title: 'Reactions', desc: 'Hover any message to react with 18 crypto-specific emojis. Click + for the full picker with Trading, Quality, Warning, and Elite-only categories.' },
  { icon: '🖼️', title: 'Share Images', desc: 'Click the image icon next to the emoji picker to share charts, screenshots, and trading setups. Add an optional caption.' },
  { icon: '🔔', title: 'Notifications', desc: 'Click the bell icon to see when someone mentions you or replies to your message. Never miss important conversations.' },
];

const INTEREST_OPTIONS = [
  // DEX Networks
  { id: 'ethereum', label: 'Ethereum', icon: '⟠' },
  { id: 'solana', label: 'Solana', icon: '◎' },
  { id: 'base', label: 'Base', icon: '🔵' },
  { id: 'bsc', label: 'BSC', icon: '🟡' },
  { id: 'arbitrum', label: 'Arbitrum', icon: '🔷' },
  { id: 'polygon', label: 'Polygon', icon: '🟣' },
  // CEX Exchanges
  { id: 'binance', label: 'Binance', icon: '🟨' },
  { id: 'coinbase', label: 'Coinbase', icon: '🟦' },
  { id: 'kraken', label: 'Kraken', icon: '🟪' },
  { id: 'bybit', label: 'Bybit', icon: '🟧' },
  { id: 'okx', label: 'OKX', icon: '⬛' },
  { id: 'kucoin', label: 'KuCoin', icon: '🟩' },
  // Topics
  { id: 'signals', label: 'Signals & Alerts', icon: '🎯' },
  { id: 'strategies', label: 'Strategies', icon: '📊' },
  { id: 'degen', label: 'Degen Plays', icon: '🎰' },
];

function GuidelinesModal({ onClose, required, onAcknowledge }) {
  const [accepting, setAccepting] = useState(false);
  const [selectedInterests, setSelectedInterests] = useState([]);

  function toggleInterest(id) {
    setSelectedInterests(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  }

  async function handleAccept() {
    setAccepting(true);
    try {
      await acknowledgeGuidelines();
      if (selectedInterests.length > 0) {
        await completeOnboarding(selectedInterests);
      }
      if (onAcknowledge) onAcknowledge();
    } catch {}
    setAccepting(false);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/70" onClick={required ? undefined : onClose} />
      <div className="relative bg-dark-800 rounded-2xl border border-dark-700/50 shadow-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto">
        <div className="sticky top-0 bg-dark-800 border-b border-dark-700/50 p-5 flex items-center justify-between z-10">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-accent-primary" /> Community Guidelines
          </h2>
          {!required && (
            <button onClick={onClose} className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-dark-700 transition-all">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {required && (
          <div className="px-5 pt-4">
            <div className="bg-accent-primary/10 border border-accent-primary/20 rounded-xl p-3 flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-accent-primary flex-shrink-0" />
              <p className="text-sm text-accent-primary font-medium">Please read and accept the community guidelines before joining chat.</p>
            </div>
          </div>
        )}

        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-400">
            haythix community is built on trust. These rules keep our channels clean, safe, and valuable for every trader. Violations are handled swiftly.
          </p>

          {/* Rules */}
          <div className="space-y-2">
            {GUIDELINES.map((g, i) => (
              <div key={i} className={`rounded-xl p-3 border ${
                g.severity === 'critical' ? 'bg-red-500/5 border-red-500/15' :
                g.severity === 'warning' ? 'bg-amber-500/5 border-amber-500/15' :
                'bg-dark-700/30 border-dark-700/50'
              }`}>
                <div className="flex items-start gap-2.5">
                  <span className="text-lg flex-shrink-0">{g.icon}</span>
                  <div>
                    <p className="text-sm font-semibold text-white">{g.title}</p>
                    <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{g.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Penalty ladder */}
          <div className="mt-4">
            <h3 className="text-sm font-bold text-gray-300 mb-2">Penalty Ladder</h3>
            <div className="space-y-1.5">
              {PENALTIES.map((p, i) => (
                <div key={i} className={`flex items-center gap-3 rounded-xl p-2.5 border ${p.color}`}>
                  <span className="text-lg">{p.icon}</span>
                  <div className="flex-1">
                    <span className="text-xs font-bold">{p.offense}</span>
                    <span className="text-xs text-gray-400 ml-2">{p.action}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pro Tips — how to use community features */}
          <div className="mt-4">
            <h3 className="text-sm font-bold text-accent-primary mb-2">Pro Tips — Get the Most Out of haythix</h3>
            <div className="space-y-2">
              {PRO_TIPS.map((tip, i) => (
                <div key={i} className="rounded-xl p-3 bg-accent-primary/5 border border-accent-primary/10">
                  <div className="flex items-start gap-2.5">
                    <span className="text-lg flex-shrink-0">{tip.icon}</span>
                    <div>
                      <p className="text-sm font-semibold text-white">{tip.title}</p>
                      <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{tip.desc}</p>
                      {tip.example && (
                        <p className="text-[10px] text-accent-primary/60 mt-1 font-mono bg-dark-700/50 rounded px-2 py-1 inline-block">
                          Example: {tip.example}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {required ? (
            <>
              {/* Interest picker */}
              <div className="mt-4">
                <h3 className="text-sm font-bold text-white mb-2">What are you interested in? <span className="text-gray-500 font-normal">(pick 2+)</span></h3>
                <div className="flex flex-wrap gap-2">
                  {INTEREST_OPTIONS.map(opt => (
                    <button key={opt.id} onClick={() => toggleInterest(opt.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium border transition-all ${
                        selectedInterests.includes(opt.id)
                          ? 'bg-accent-primary/15 border-accent-primary/30 text-accent-primary'
                          : 'bg-dark-700/50 border-dark-700 text-gray-400 hover:border-dark-500'
                      }`}>
                      <span>{opt.icon}</span> {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={handleAccept} disabled={accepting || selectedInterests.length < 2}
                className="w-full py-3 rounded-xl font-bold text-sm bg-accent-primary text-dark-900 hover:bg-accent-primary/90 disabled:opacity-40 transition-all flex items-center justify-center gap-2">
                {accepting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                {selectedInterests.length < 2 ? 'Select at least 2 interests' : 'Join the Community'}
              </button>
            </>
          ) : (
            <p className="text-[10px] text-gray-600 text-center mt-3">
              By participating in haythix community channels, you agree to these guidelines.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Moderation Alert Modal (shown to warned/muted/banned users) ────────────

function ModerationAlertModal({ alert, onAcknowledge }) {
  const config = {
    streak_milestone: {
      icon: alert.emoji || '🔥', title: 'Streak Milestone!',
      color: 'border-orange-500/30 bg-orange-500/5',
      headerBg: 'bg-gradient-to-r from-orange-500/15 to-yellow-500/15',
      headerText: 'text-orange-400',
      desc: '',
      btnColor: 'bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-400 hover:to-yellow-400 text-dark-900',
    },
    warn: {
      icon: '⚠️', title: 'You Have Been Warned',
      color: 'border-amber-500/30 bg-amber-500/5',
      headerBg: 'bg-amber-500/10',
      headerText: 'text-amber-400',
      desc: 'This is a formal warning from a moderator. Continued violations will result in a mute or permanent ban.',
      btnColor: 'bg-amber-500 hover:bg-amber-400 text-dark-900',
    },
    mute: {
      icon: '🔇', title: 'You Have Been Muted',
      color: 'border-orange-500/30 bg-orange-500/5',
      headerBg: 'bg-orange-500/10',
      headerText: 'text-orange-400',
      desc: `You cannot send messages until ${alert.mutedUntil ? new Date(alert.mutedUntil).toLocaleString() : 'the mute expires'}. Duration: ${alert.duration || 'N/A'}.`,
      btnColor: 'bg-orange-500 hover:bg-orange-400 text-dark-900',
    },
    ban: {
      icon: '🚫', title: 'You Have Been Banned',
      color: 'border-red-500/30 bg-red-500/5',
      headerBg: 'bg-red-500/10',
      headerText: 'text-red-400',
      desc: 'You have been permanently banned from the community. If you believe this is a mistake, contact support.',
      btnColor: 'bg-red-500 hover:bg-red-400 text-white',
    },
    unban: {
      icon: '🎉', title: 'Welcome Back!',
      color: 'border-emerald-500/30 bg-emerald-500/5',
      headerBg: 'bg-emerald-500/10',
      headerText: 'text-emerald-400',
      desc: '',
      btnColor: 'bg-emerald-500 hover:bg-emerald-400 text-dark-900',
    },
  };

  const c = config[alert.type] || config.warn;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/80" />
      <div className={`relative rounded-2xl border shadow-2xl w-full max-w-md overflow-hidden ${c.color}`}>
        {/* Header */}
        <div className={`${c.headerBg} p-6 text-center`}>
          <span className="text-5xl block mb-3">{c.icon}</span>
          <h2 className={`text-xl font-bold ${c.headerText}`}>{c.title}</h2>
        </div>

        {/* Body */}
        <div className="bg-dark-800 p-6 space-y-4">
          {alert.type === 'streak_milestone' ? (
            <div className="bg-orange-500/5 border border-orange-500/15 rounded-xl p-4 text-center">
              <p className="text-4xl mb-2">{alert.emoji || '🔥'}</p>
              <p className="text-sm font-bold text-orange-300 mb-1">{alert.name || 'Streak Badge'}</p>
              <p className="text-sm text-gray-300 leading-relaxed">{alert.reason}</p>
            </div>
          ) : alert.type === 'unban' ? (
            <div className="bg-emerald-500/5 border border-emerald-500/15 rounded-xl p-4">
              <p className="text-sm text-gray-300 leading-relaxed">{alert.reason}</p>
            </div>
          ) : (
            <>
              <div className="bg-dark-700/50 rounded-xl p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Reason</p>
                <p className="text-sm text-white font-medium">{alert.reason || 'Community guidelines violation'}</p>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed">{c.desc}</p>
            </>
          )}

          {alert.type === 'warn' && (
            <div className="bg-dark-700/30 rounded-xl p-3 border border-dark-700/50">
              <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Penalty Ladder</p>
              <div className="space-y-1 text-xs">
                <p className="text-amber-400 font-medium">1st Warning → Message deleted</p>
                <p className="text-orange-400">2nd Offense → Muted for 24 hours</p>
                <p className="text-red-400">3rd Offense → Permanent ban</p>
              </div>
            </div>
          )}

          <button onClick={() => onAcknowledge(alert.id)}
            className={`w-full py-3 rounded-xl font-bold text-sm transition-all ${c.btnColor}`}>
            {alert.type === 'streak_milestone' ? 'Keep the Streak Going!' :
             alert.type === 'unban' ? 'Thank You — Let\'s Go!' :
             alert.type === 'ban' ? 'I Understand' : 'I Understand — I\'ll Follow the Rules'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Banned Users Panel (admin only) ────────────────────────────────────────

function BannedUsersModal({ onClose, onUnban }) {
  const [banned, setBanned] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getBannedUsers()
      .then(d => setBanned(d?.bannedUsers || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/70" onClick={onClose} />
      <div className="relative bg-dark-800 rounded-2xl border border-dark-700/50 shadow-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto">
        <div className="sticky top-0 bg-dark-800 border-b border-dark-700/50 p-5 flex items-center justify-between z-10">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <UserX className="w-5 h-5 text-red-400" /> Banned Users
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-dark-700 transition-all">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-accent-primary animate-spin" /></div>
          ) : banned.length === 0 ? (
            <div className="text-center py-8">
              <span className="text-3xl mb-2 block">✅</span>
              <p className="text-gray-400 text-sm">No banned users</p>
            </div>
          ) : (
            <div className="space-y-3">
              {banned.map(u => (
                <div key={u.userId} className="bg-dark-700/50 rounded-xl p-4 border border-dark-700/50">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-sm font-semibold text-white">{u.userName}</p>
                      <p className="text-[10px] text-gray-500">Banned {u.bannedAt ? new Date(u.bannedAt).toLocaleDateString() : 'N/A'}</p>
                    </div>
                    <button onClick={() => { onUnban(u.userId); setBanned(prev => prev.filter(b => b.userId !== u.userId)); }}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 transition-all">
                      Unban
                    </button>
                  </div>
                  <div className="bg-dark-800/50 rounded-lg p-2.5 mb-2">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5">Ban Reason</p>
                    <p className="text-xs text-gray-300">{u.reason}</p>
                  </div>
                  {u.unbanRequest && (
                    <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-2.5">
                      <p className="text-[10px] text-blue-400 uppercase tracking-wider mb-0.5 font-bold">Unban Request</p>
                      <p className="text-xs text-gray-300">{u.unbanRequest.reason}</p>
                      <p className="text-[9px] text-gray-500 mt-1">Requested {new Date(u.unbanRequest.requestedAt).toLocaleDateString()}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Locked Room Upgrade Modal ──────────────────────────────────────────────

const TIER_PRICES = { starter: '$10', trader: '$49.99', pro: '$75.99', elite: '$149.99' };

function LockedRoomModal({ room, onClose }) {
  if (!room) return null;
  const requiredTier = room.requiredTier || room.tier || 'trader';
  const perks = Array.isArray(room.perks) ? room.perks : [];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/70" onClick={onClose} />
      <div className="relative bg-dark-800 rounded-2xl border border-dark-700/50 shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 p-6 text-center border-b border-dark-700/50">
          <div className="w-16 h-16 rounded-2xl bg-amber-500/15 flex items-center justify-center text-3xl mx-auto mb-3">
            {room.icon}
          </div>
          <h2 className="text-lg font-bold text-white">{room.name}</h2>
          <p className="text-sm text-gray-400 mt-1">{room.description}</p>
          <div className="flex items-center justify-center gap-1.5 mt-2">
            <Lock className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-xs font-bold text-amber-400 uppercase">Requires {requiredTier} tier</span>
          </div>
        </div>

        {/* Perks */}
        <div className="p-6 space-y-4">
          {perks.length > 0 && (
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">What you unlock</p>
              <div className="space-y-1.5">
                {perks.map((perk, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span className="text-gray-300">{perk}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-dark-700/50 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-white">{TIER_PRICES[requiredTier] || '$49.99'}<span className="text-sm text-gray-500 font-normal">/mo</span></p>
            <p className="text-[10px] text-gray-500 mt-0.5">Billed annually · Cancel anytime</p>
          </div>

          <button onClick={() => { onClose(); window.location.href = '/pricing'; }}
            className="w-full py-3 rounded-xl text-sm font-bold bg-gradient-to-r from-amber-500 to-orange-500 text-dark-900 hover:from-amber-400 hover:to-orange-400 transition-all">
            Upgrade to {requiredTier.charAt(0).toUpperCase() + requiredTier.slice(1)}
          </button>
          <button onClick={onClose} className="w-full py-2 text-sm text-gray-500 hover:text-gray-400 transition-colors">
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Create Poll/Prediction Modal ───────────────────────────────────────────

const PREDICTION_ASSETS = ['BTC', 'ETH', 'SOL', 'BNB', 'ARB', 'AVAX', 'LINK', 'DOGE', 'PEPE', 'XRP', 'ADA', 'UNI', 'OP'];

function CreatePollModal({ roomId, onClose, onCreated }) {
  const [tab, setTab] = useState('poll');
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [asset, setAsset] = useState('BTC');
  const [predType, setPredType] = useState('price_above');
  const [targetPrice, setTargetPrice] = useState('');
  const [resolveAt, setResolveAt] = useState('');
  const [creating, setCreating] = useState(false);

  async function handleCreate() {
    setCreating(true);
    try {
      if (tab === 'poll') {
        const validOpts = options.filter(o => o.trim());
        if (!question.trim() || validOpts.length < 2) return;
        await createPoll(roomId, question, validOpts);
      } else {
        if (!targetPrice || !resolveAt) return;
        await createPrediction(roomId, asset, predType, parseFloat(targetPrice), resolveAt);
      }
      onCreated();
      onClose();
    } catch (err) {
      alert(err.message || 'Failed to create');
    }
    setCreating(false);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/70" onClick={onClose} />
      <div className="relative bg-dark-800 rounded-2xl border border-dark-700/50 shadow-2xl w-full max-w-md">
        <div className="p-5 border-b border-dark-700/50 flex items-center justify-between">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-accent-primary" /> Create
          </h2>
          <button onClick={onClose} className="p-1.5 text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {/* Tab toggle */}
        <div className="flex mx-5 mt-4 bg-dark-700 rounded-xl p-1">
          <button onClick={() => setTab('poll')} className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${tab === 'poll' ? 'bg-accent-primary text-dark-900' : 'text-gray-400'}`}>📊 Poll</button>
          <button onClick={() => setTab('prediction')} className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${tab === 'prediction' ? 'bg-accent-primary text-dark-900' : 'text-gray-400'}`}>🔮 Prediction</button>
        </div>

        <div className="p-5 space-y-3">
          {tab === 'poll' ? (
            <>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Question</label>
                <input type="text" value={question} onChange={e => setQuestion(e.target.value)} maxLength={200}
                  placeholder="Will BTC hit $150K by EOY?"
                  className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-600 focus:border-accent-primary focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Options (2-6)</label>
                {options.map((opt, i) => (
                  <div key={i} className="flex gap-2 mb-1.5">
                    <input type="text" value={opt} onChange={e => { const n = [...options]; n[i] = e.target.value; setOptions(n); }}
                      placeholder={`Option ${i + 1}`} maxLength={100}
                      className="flex-1 bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-sm placeholder-gray-600 focus:border-accent-primary focus:outline-none" />
                    {options.length > 2 && (
                      <button onClick={() => setOptions(options.filter((_, j) => j !== i))} className="text-gray-600 hover:text-red-400"><X className="w-4 h-4" /></button>
                    )}
                  </div>
                ))}
                {options.length < 6 && (
                  <button onClick={() => setOptions([...options, ''])} className="text-xs text-accent-primary hover:text-accent-primary/80">+ Add option</button>
                )}
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Asset</label>
                <select value={asset} onChange={e => setAsset(e.target.value)}
                  className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm focus:border-accent-primary focus:outline-none">
                  {PREDICTION_ASSETS.map(a => <option key={a} value={a}>${a}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Prediction</label>
                <select value={predType} onChange={e => setPredType(e.target.value)}
                  className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm focus:border-accent-primary focus:outline-none">
                  <option value="price_above">Price will be ABOVE</option>
                  <option value="price_below">Price will be BELOW</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Target Price ($)</label>
                <input type="number" value={targetPrice} onChange={e => setTargetPrice(e.target.value)}
                  placeholder="150000" step="any"
                  className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-600 focus:border-accent-primary focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Resolves At</label>
                <input type="datetime-local" value={resolveAt} onChange={e => setResolveAt(e.target.value)}
                  min={new Date(Date.now() + 3600000).toISOString().slice(0, 16)}
                  className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm focus:border-accent-primary focus:outline-none" />
              </div>
            </>
          )}

          <button onClick={handleCreate} disabled={creating}
            className="w-full py-3 rounded-xl text-sm font-bold bg-accent-primary text-dark-900 hover:bg-accent-primary/90 disabled:opacity-40 transition-all flex items-center justify-center gap-2">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />}
            Create {tab === 'poll' ? 'Poll' : 'Prediction'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Tip Modal ──────────────────────────────────────────────────────────────

const TIP_CHAINS = [
  { id: 'ethereum', name: 'Ethereum', icon: '⟠' },
  { id: 'base', name: 'Base', icon: '🔵' },
  { id: 'polygon', name: 'Polygon', icon: '🟣' },
  { id: 'arbitrum', name: 'Arbitrum', icon: '🔷' },
];

const TIP_AMOUNTS = [25, 50, 75, 100];

// USDC contract addresses per chain
const USDC_ADDRESSES = {
  ethereum: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
  base: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
  polygon: '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174',
  arbitrum: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831',
};
const TREASURY = '0x3A6163B029081e1C4b94CC98f2A6895CA314D76a';

// Detect mobile/tablet (no MetaMask extension available)
const isMobileDevice = () => /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ||
  (navigator.maxTouchPoints > 1 && !window.ethereum);

// Build wallet deep-links for ERC-20 transfer (EIP-681)
const CHAIN_IDS = { ethereum: 1, base: 8453, polygon: 137, arbitrum: 42161 };

// Supported mobile wallets with deep-link builders
const MOBILE_WALLETS = [
  {
    id: 'metamask',
    name: 'MetaMask',
    icon: '🦊',
    color: 'from-orange-500 to-amber-500',
    buildLink: (chain, amount) => {
      const usdcContract = USDC_ADDRESSES[chain];
      const chainId = CHAIN_IDS[chain];
      const amountWei = BigInt(Math.round(amount * 1e6)).toString();
      return `https://metamask.app.link/send/${usdcContract}@${chainId}/transfer?address=${TREASURY}&uint256=${amountWei}`;
    },
  },
  {
    id: 'trust',
    name: 'Trust Wallet',
    icon: '🛡️',
    color: 'from-blue-500 to-blue-600',
    buildLink: (chain, amount) => {
      const usdcContract = USDC_ADDRESSES[chain];
      const chainId = CHAIN_IDS[chain];
      const amountWei = BigInt(Math.round(amount * 1e6)).toString();
      return `https://link.trustwallet.com/send?asset=c${chainId}_t${usdcContract}&address=${TREASURY}&amount=${amount}`;
    },
  },
  {
    id: 'coinbase',
    name: 'Coinbase Wallet',
    icon: '🔵',
    color: 'from-blue-600 to-indigo-600',
    buildLink: (chain, amount) => {
      const usdcContract = USDC_ADDRESSES[chain];
      const chainId = CHAIN_IDS[chain];
      const amountWei = BigInt(Math.round(amount * 1e6)).toString();
      // Coinbase Wallet uses cbwallet:// scheme with EIP-681
      return `https://go.cb-w.com/send?address=${usdcContract}&chain=${chainId}&function=transfer(address,uint256)&args=${TREASURY},${amountWei}`;
    },
  },
];

function buildWalletDeepLink(walletId, chain, amount) {
  const wallet = MOBILE_WALLETS.find(w => w.id === walletId);
  return wallet ? wallet.buildLink(chain, amount) : null;
}

function TipModal({ target, onClose, onSend }) {
  const [amount, setAmount] = useState(25);
  const [customAmount, setCustomAmount] = useState('');
  const [chain, setChain] = useState('ethereum');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [txStatus, setTxStatus] = useState(null); // 'pending' | 'sent' | 'failed'
  const [selectedWallet, setSelectedWallet] = useState('metamask');

  const finalAmount = customAmount ? parseFloat(customAmount) : amount;
  const isMobile = isMobileDevice();

  // Desktop: send via MetaMask extension (window.ethereum)
  async function handleDesktopSend() {
    if (!finalAmount || finalAmount < 25 || finalAmount > 10000) return;
    setSending(true);

    let tipResult;
    try {
      tipResult = await createTip(target.userId, finalAmount, chain, note, true);
    } catch (err) {
      alert(err.message || 'Failed to create tip');
      setSending(false);
      return;
    }

    if (window.ethereum) {
      try {
        setTxStatus('pending');
        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
        if (!accounts?.[0]) throw new Error('No wallet connected');

        const usdcContract = USDC_ADDRESSES[chain];
        if (!usdcContract) throw new Error('USDC not supported on this chain');

        const amountInSmallestUnit = BigInt(Math.round(finalAmount * 1e6));
        const amountHex = '0x' + amountInSmallestUnit.toString(16);
        const transferData = '0xa9059cbb' +
          TREASURY.slice(2).padStart(64, '0') +
          amountHex.slice(2).padStart(64, '0');

        const chainIds = { ethereum: '0x1', base: '0x2105', polygon: '0x89', arbitrum: '0xa4b1' };
        try {
          await window.ethereum.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: chainIds[chain] }] });
        } catch {}

        const txHash = await window.ethereum.request({
          method: 'eth_sendTransaction',
          params: [{ from: accounts[0], to: usdcContract, data: transferData, value: '0x0' }],
        });

        setTxStatus('sent');
        setPaymentInfo({ ...tipResult, txHash, autoSent: true });

        try {
          await fetch(`${import.meta.env.VITE_API_URL || '/api'}/wallet/verify-tx`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
            body: JSON.stringify({ txHash, chain, tipId: tipResult.tipId }),
          });
        } catch {}
      } catch (err) {
        setTxStatus('failed');
        setPaymentInfo(tipResult);
      }
    } else {
      setPaymentInfo(tipResult);
    }
    setSending(false);
  }

  // Mobile: create tip on backend, then open wallet app via deep-link
  async function handleMobileSend() {
    if (!finalAmount || finalAmount < 25 || finalAmount > 10000) return;
    setSending(true);

    let tipResult;
    try {
      tipResult = await createTip(target.userId, finalAmount, chain, note, true);
    } catch (err) {
      alert(err.message || 'Failed to create tip');
      setSending(false);
      return;
    }

    // Open selected wallet app with the USDC transfer pre-filled
    const deepLink = buildWalletDeepLink(selectedWallet, chain, finalAmount);
    if (deepLink) window.location.href = deepLink;

    // Show payment confirmation UI after a short delay (wallet app opens)
    setTimeout(() => {
      setPaymentInfo({ ...tipResult, mobileDeepLink: true, walletUsed: selectedWallet });
      setSending(false);
    }, 1500);
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/70" onClick={onClose} />
      <div className="relative bg-dark-800 rounded-2xl border border-dark-700/50 shadow-2xl w-full max-w-sm overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-emerald-500/60 via-green-400/80 to-emerald-500/60" />
        <div className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              💰 {paymentInfo ? 'Payment Status' : 'Send Tip'}
            </h2>
            <button onClick={onClose} className="text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
          </div>

          {!paymentInfo ? (
            <>
              <p className="text-sm text-gray-400 mb-4">
                Tip <span className="text-white font-semibold">{target.userName}</span> with USDC
              </p>

              <div className="grid grid-cols-4 gap-1.5 mb-3">
                {TIP_AMOUNTS.map(a => (
                  <button key={a} onClick={() => { setAmount(a); setCustomAmount(''); }}
                    className={`py-2.5 rounded-lg text-xs font-bold transition-all ${
                      amount === a && !customAmount ? 'bg-emerald-500 text-dark-900' : 'bg-dark-700 text-gray-400 hover:bg-dark-600'
                    }`}>${a}</button>
                ))}
              </div>

              <div className="relative mb-3">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">$</span>
                <input type="number" value={customAmount} onChange={e => { setCustomAmount(e.target.value); setAmount(0); }}
                  placeholder="Custom amount (min $25)" min={25} max={10000} step="any"
                  className="w-full bg-dark-700 border border-dark-600 rounded-xl pl-7 pr-4 py-2.5 text-white text-sm placeholder-gray-600 focus:border-emerald-500 focus:outline-none" />
              </div>

              <div className="mb-3">
                <p className="text-[10px] text-gray-500 mb-1.5">Pay with USDC on</p>
                <div className="flex gap-1.5">
                  {TIP_CHAINS.map(c => (
                    <button key={c.id} onClick={() => setChain(c.id)}
                      className={`flex-1 py-2 rounded-lg text-[10px] font-medium transition-all flex flex-col items-center gap-0.5 ${
                        chain === c.id ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' : 'bg-dark-700 text-gray-500 border border-dark-600'
                      }`}>
                      <span>{c.icon}</span>
                      <span>{c.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              <input type="text" value={note} onChange={e => setNote(e.target.value)}
                placeholder="Add a note (optional)" maxLength={200}
                className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-600 focus:border-emerald-500 focus:outline-none mb-3" />

              <div className="bg-emerald-500/5 rounded-xl p-3 border border-emerald-500/15 mb-3">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-400">Tip amount</span>
                  <span className="text-white font-bold">${finalAmount || 0} USDC</span>
                </div>
                <div className="flex justify-between text-xs mt-1">
                  <span className="text-gray-500">Creator receives (80%)</span>
                  <span className="text-emerald-400 font-bold">${((finalAmount || 0) * 0.8).toFixed(2)}</span>
                </div>
              </div>

              {/* Send button — desktop vs mobile */}
              {isMobile ? (
                <>
                  {/* Mobile wallet picker */}
                  <div className="mb-3">
                    <p className="text-[10px] text-gray-500 mb-1.5">Open with</p>
                    <div className="grid grid-cols-3 gap-1.5">
                      {MOBILE_WALLETS.map(w => (
                        <button key={w.id} onClick={() => setSelectedWallet(w.id)}
                          className={`py-2.5 rounded-lg text-[10px] font-medium transition-all flex flex-col items-center gap-1 ${
                            selectedWallet === w.id
                              ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                              : 'bg-dark-700 text-gray-500 border border-dark-600'
                          }`}>
                          <span className="text-base">{w.icon}</span>
                          <span>{w.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <button onClick={handleMobileSend} disabled={sending || !finalAmount || finalAmount < 25}
                    className={`w-full py-3 rounded-xl text-sm font-bold bg-gradient-to-r ${MOBILE_WALLETS.find(w => w.id === selectedWallet)?.color || 'from-emerald-500 to-emerald-600'} text-white hover:opacity-90 disabled:opacity-40 transition-all flex items-center justify-center gap-2`}>
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="text-base">{MOBILE_WALLETS.find(w => w.id === selectedWallet)?.icon}</span>}
                    Open in {MOBILE_WALLETS.find(w => w.id === selectedWallet)?.name} — ${finalAmount || 0}
                  </button>
                  <p className="text-[10px] text-gray-600 mt-2 text-center">
                    This will open your wallet app with the USDC payment pre-filled
                  </p>
                </>
              ) : (
                <button onClick={handleDesktopSend} disabled={sending || !finalAmount || finalAmount < 25}
                  className="w-full py-3 rounded-xl text-sm font-bold bg-emerald-500 text-dark-900 hover:bg-emerald-400 disabled:opacity-40 transition-all flex items-center justify-center gap-2">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <DollarSign className="w-4 h-4" />}
                  Send ${finalAmount || 0} USDC
                </button>
              )}
            </>
          ) : (
            <>
              {paymentInfo.autoSent ? (
                /* Auto-sent via MetaMask extension (desktop) */
                <div className="text-center space-y-4">
                  <div className="bg-emerald-500/10 rounded-2xl p-6 border border-emerald-500/20">
                    <span className="text-4xl block mb-3">✅</span>
                    <h3 className="text-lg font-bold text-emerald-400">Payment Sent!</h3>
                    <p className="text-sm text-gray-400 mt-1">${paymentInfo.amount} USDC sent to {target.userName}</p>
                  </div>
                  <div className="bg-dark-700/50 rounded-xl p-3">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Transaction Hash</p>
                    <p className="text-[10px] text-white font-mono break-all select-all">{paymentInfo.txHash}</p>
                  </div>
                  <p className="text-xs text-gray-500">{target.userName} will be notified once the transaction confirms on-chain (usually 1-5 minutes).</p>
                </div>
              ) : paymentInfo.mobileDeepLink ? (
                /* Mobile — wallet app was opened */
                (() => {
                  const usedWallet = MOBILE_WALLETS.find(w => w.id === paymentInfo.walletUsed) || MOBILE_WALLETS[0];
                  return (
                    <div className="text-center space-y-4">
                      <div className="bg-amber-500/10 rounded-2xl p-6 border border-amber-500/20">
                        <span className="text-4xl block mb-3">{usedWallet.icon}</span>
                        <h3 className="text-lg font-bold text-amber-400">Complete in {usedWallet.name}</h3>
                        <p className="text-sm text-gray-400 mt-2">
                          {usedWallet.name} should have opened with a <span className="text-white font-semibold">${paymentInfo.amount} USDC</span> transfer to {target.userName}.
                        </p>
                      </div>
                      <div className="bg-dark-700/50 rounded-xl p-3 space-y-2">
                        <p className="text-xs text-gray-300 font-medium">In {usedWallet.name}:</p>
                        <ol className="text-[11px] text-gray-400 space-y-1 list-decimal list-inside">
                          <li>Review the transaction details</li>
                          <li>Make sure you're on <span className="text-white">{chain.charAt(0).toUpperCase() + chain.slice(1)}</span></li>
                          <li>Tap <span className="text-emerald-400 font-medium">Confirm</span> to send</li>
                        </ol>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => { const link = buildWalletDeepLink(paymentInfo.walletUsed, chain, finalAmount); if (link) window.location.href = link; }}
                          className="flex-1 py-2.5 rounded-xl text-xs font-bold bg-amber-500/15 text-amber-400 border border-amber-500/25 hover:bg-amber-500/25 transition-all">
                          Re-open {usedWallet.name}
                        </button>
                      </div>
                      <p className="text-[10px] text-gray-600">
                        {target.userName} will be notified automatically once the payment confirms on-chain.
                      </p>
                    </div>
                  );
                })()
              ) : (
                /* Manual fallback — desktop MetaMask rejected or no extension */
                <>
                  {txStatus === 'failed' && (
                    <div className="bg-amber-500/5 rounded-xl p-3 border border-amber-500/15 mb-3">
                      <p className="text-xs text-amber-400">Transaction was cancelled or failed. You can send manually instead:</p>
                    </div>
                  )}
                  <div className="bg-emerald-500/5 rounded-xl p-4 border border-emerald-500/15 mb-4 text-center">
                    <p className="text-xs text-gray-400 mb-1">Send exactly</p>
                    <p className="text-2xl font-bold text-emerald-400">${paymentInfo.amount} USDC</p>
                    <p className="text-xs text-gray-500 mt-1">on {chain.charAt(0).toUpperCase() + chain.slice(1)}</p>
                  </div>

                  <div className="space-y-3 mb-4">
                    <div className="bg-dark-700/50 rounded-xl p-3">
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Send USDC to this address</p>
                      <p className="text-xs text-white font-mono break-all select-all">{paymentInfo.payToWallet}</p>
                    </div>
                    <div className="bg-dark-700/50 rounded-xl p-3">
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Include this memo</p>
                      <p className="text-sm text-accent-primary font-mono font-bold select-all">{paymentInfo.memo}</p>
                    </div>
                  </div>

                  <div className="bg-amber-500/5 rounded-xl p-3 border border-amber-500/15 mb-4">
                    <p className="text-[10px] text-amber-400 font-bold mb-1">⚠️ Important</p>
                    <ul className="text-[10px] text-gray-400 space-y-0.5">
                      <li>• Send exactly ${paymentInfo.amount} USDC</li>
                      <li>• Include the memo so we can match your payment</li>
                      <li>• Payment expires in 1 hour</li>
                      <li>• {target.userName} will be notified once confirmed</li>
                    </ul>
                  </div>
                </>
              )}

              <button onClick={onClose}
                className="w-full py-3 rounded-xl text-sm font-bold bg-dark-700 text-white hover:bg-dark-600 transition-all">
                {paymentInfo.autoSent ? 'Close' : paymentInfo.mobileDeepLink ? "Done — I've Confirmed in My Wallet" : "Done — I've Sent the Payment"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function CreateChannelModal({ onClose, onCreate }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('💬');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  async function handleCreate() {
    if (!name.trim()) return;
    setCreating(true);
    setError(null);
    try {
      await onCreate(name.trim(), description.trim(), icon);
      onClose();
    } catch (err) {
      setError(err.message);
    }
    setCreating(false);
  }

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-dark-800 rounded-2xl border border-dark-700 max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-white">Create Channel</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1.5">Channel Name</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} maxLength={30}
              placeholder="e.g., Meme Coins, DeFi Alpha"
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-600 focus:border-accent-primary focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1.5">Description (optional)</label>
            <input type="text" value={description} onChange={e => setDescription(e.target.value)} maxLength={100}
              placeholder="What's this channel about?"
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-600 focus:border-accent-primary focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1.5">Icon</label>
            <div className="flex flex-wrap gap-2">
              {CHANNEL_ICONS.map(e => (
                <button key={e} onClick={() => setIcon(e)}
                  className={`w-9 h-9 rounded-lg text-lg flex items-center justify-center transition-all ${
                    icon === e ? 'bg-accent-primary/20 border border-accent-primary/40 scale-110' : 'bg-dark-700 hover:bg-dark-600'
                  }`}>{e}</button>
              ))}
            </div>
          </div>
        </div>

        {error && (
          <div className="mt-3 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2 text-xs text-red-400 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" /> {error}
          </div>
        )}

        <div className="flex justify-end gap-3 mt-5">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm text-gray-400 border border-dark-600 hover:text-white transition-all">Cancel</button>
          <button onClick={handleCreate} disabled={!name.trim() || creating}
            className="px-4 py-2 rounded-xl text-sm font-semibold bg-accent-primary text-dark-900 hover:bg-accent-primary/90 disabled:opacity-40 transition-all flex items-center gap-1.5">
            {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Create
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Message Bubble ──────────────────────────────────────────────────────────

function MessageBubble({ message, isOwn, myRole, onModAction, onAssignRole, reactions: serverReactions, onReact, onReply, onTip, allMessages }) {
  const tb = TIER_BADGE[message.userTier] || TIER_BADGE.starter;
  const crb = CHAT_ROLE_BADGE[message.chatRole];
  const msgReactions = serverReactions || {};
  const [showModMenuOpen, setShowModMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const nameRef = useRef(null);

  // System messages
  if (message.isSystem) {
    return (
      <div className="px-4 py-2">
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl px-4 py-2 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0" />
          <p className="text-xs text-amber-300">{message.content}</p>
          <span className="text-[9px] text-gray-600 ml-auto flex-shrink-0">{timeAgo(message.timestamp)}</span>
        </div>
      </div>
    );
  }

  // Deleted messages
  if (message.deleted) {
    return (
      <div className="px-4 py-2 opacity-40">
        <p className="text-xs text-gray-600 italic">Message removed by a moderator</p>
      </div>
    );
  }

  function handleReact(key) {
    if (onReact) onReact(message.messageId, key);
  }

  const totalReactions = Object.values(msgReactions).reduce((s, v) => s + (typeof v === 'object' ? v.count : v), 0);
  const myPerms = { owner: { del: true, warn: true, mute: true, ban: true }, superadmin: { del: true, warn: true, mute: true, ban: true }, admin: { del: true, warn: true, mute: true, ban: true }, mod: { del: true, warn: true, mute: true, ban: false } }[myRole] || {};
  const canModOthers = Object.values(myPerms).some(Boolean) && !isOwn && message.userId !== 'system';
  const canDeleteOwn = isOwn && message.userId !== 'system';
  const showModMenu = canModOthers || canDeleteOwn;

  return (
    <div className={`group flex gap-3 px-4 py-2 hover:bg-dark-800/40 transition-colors relative ${
      message.isBot ? 'border-l-2 border-l-accent-primary/50 bg-accent-primary/[0.02]' :
      message.callData ? (message.callData.action === 'bullish' ? 'border-l-2 border-l-emerald-500/50' : 'border-l-2 border-l-red-500/50') : ''
    }`}>
      {/* Avatar */}
      <div className="mt-0.5">
        <UserAvatar name={message.userName} chatRole={message.chatRole} avatar={message.avatar} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
          <span ref={nameRef} onClick={() => message.userId !== 'system' && setShowUserMenu(!showUserMenu)}
            className={`text-sm font-semibold cursor-pointer hover:underline relative ${
            message.chatRole === 'owner' ? 'text-red-400' :
            message.chatRole === 'superadmin' ? 'text-orange-400' :
            message.chatRole === 'admin' ? 'text-amber-400' :
            isOwn ? 'text-accent-primary' : 'text-white'
          }`}>
            {message.userName || 'User'}
            {showUserMenu && !isOwn && (
              <div style={{ position: 'absolute', left: 0, top: '100%', zIndex: 50 }}>
                <UserContextMenu
                  targetUser={{ userId: message.userId, userName: message.userName, chatRole: message.chatRole || 'member', userTier: message.userTier }}
                  myRole={myRole}
                  isSelf={isOwn}
                  onClose={() => setShowUserMenu(false)}
                  onModAction={(a, p) => { onModAction(a, { ...p, roomId: message.roomId }); setShowUserMenu(false); }}
                  onAssignRole={(uid, role) => { onAssignRole(uid, role); setShowUserMenu(false); }}
                />
              </div>
            )}
          </span>
          {/* Chat role badge */}
          {crb && (
            <span className={`text-[8px] font-bold px-1.5 py-px rounded flex items-center gap-0.5 ${crb.bg} ${crb.text}`}>
              <crb.icon className="w-2.5 h-2.5" /> {crb.label}
            </span>
          )}
          {/* Tier badge */}
          <span className={`text-[8px] font-bold px-1.5 py-px rounded ${tb.bg} ${tb.text} uppercase`}>
            {message.userTier}
          </span>
          {/* Streak badge */}
          {(message.streak || 0) > 0 && (
            <span className={`text-[8px] font-bold px-1.5 py-px rounded flex items-center gap-0.5 ${
              message.streak >= 100 ? 'bg-gradient-to-r from-orange-500/25 to-yellow-500/25 text-yellow-300 ring-1 ring-yellow-500/30' :
              message.streak >= 30 ? 'bg-orange-500/15 text-orange-400' :
              message.streak >= 7 ? 'bg-amber-500/15 text-amber-400' :
              'bg-orange-500/10 text-orange-300/70'
            }`}>
              <Flame className="w-2.5 h-2.5" /> {message.streak}d
            </span>
          )}
          <span className="text-[10px] text-gray-600">{timeAgo(message.timestamp)}</span>
        </div>

        {/* Reply context */}
        {message.replyTo && (() => {
          const original = (allMessages || []).find(m => m.messageId === message.replyTo);
          return original ? (
            <div className="mb-1 pl-3 border-l-2 border-accent-primary/30 rounded-r">
              <p className="text-[10px] text-accent-primary/60">↩ {original.userName}</p>
              <p className="text-[11px] text-gray-500 truncate">{original.content?.slice(0, 80)}</p>
            </div>
          ) : null;
        })()}

        {/* Call tag */}
        {message.callData && (
          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold mb-1 border ${
            message.callData.action === 'bullish'
              ? 'bg-emerald-500/10 border-emerald-500/25 text-emerald-400'
              : 'bg-red-500/10 border-red-500/25 text-red-400'
          }`}>
            <span>{message.callData.action === 'bullish' ? '📈' : '📉'}</span>
            <span>{message.callData.action.toUpperCase()}</span>
            <span className="font-bold">${message.callData.token}</span>
            <span className="text-gray-500">@</span>
            <span className="text-white">${message.callData.entryPrice}</span>
            {message.callData.targetPrice && (
              <><span className="text-gray-500">→</span><span className="text-white">${message.callData.targetPrice}</span></>
            )}
          </div>
        )}

        {/* Message content with mention highlighting */}
        {message.content && (
          <p className="text-sm text-gray-300 leading-relaxed break-words">
            {message.content.split(/(@[a-zA-Z0-9_ ]{2,30})/g).map((part, i) =>
              part.startsWith('@') ? (
                <span key={i} className="bg-accent-primary/15 text-accent-primary px-1 rounded font-medium">{part}</span>
              ) : (
                <span key={i}>{part}</span>
              )
            )}
          </p>
        )}

        {/* Image attachment */}
        {message.image && <ChatImage imageId={message.image} roomId={message.roomId} />}

        {/* Voice note */}
        {message.voice && <VoiceNotePlayer voice={message.voice} roomId={message.roomId} />}

        {/* Trade snapshot card */}
        {message.tradeSnapshot && <TradeSnapshotCard snapshot={message.tradeSnapshot} />}

        {/* Reactions */}
        {totalReactions > 0 && (
          <div className="flex items-center gap-1 mt-1.5 flex-wrap">
            {Object.entries(msgReactions).filter(([_, v]) => (typeof v === 'object' ? v.count : v) > 0).map(([key, val]) => {
              const r = REACTIONS.find(r => r.key === key);
              const count = typeof val === 'object' ? val.count : val;
              const mine = typeof val === 'object' ? val.mine : false;
              return r ? (
                <button key={key} onClick={() => handleReact(key)} title={`${r.label}${r.tip ? ' — ' + r.tip : ''}${mine ? ' (click to remove)' : ''}`}
                  className={`text-xs rounded-lg px-1.5 py-0.5 flex items-center gap-0.5 transition-all border ${
                    mine ? 'bg-accent-primary/15 border-accent-primary/30 text-accent-primary' : 'bg-dark-700 border-dark-700 hover:bg-dark-600 text-gray-400'
                  }`}>
                  {r.emoji} <span>{count}</span>
                </button>
              ) : null;
            })}
          </div>
        )}
      </div>

      {/* Hover actions: reactions + moderation */}
      <div className="absolute right-3 top-1 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
        {/* Reply + Tip buttons */}
        {!message.isSystem && !isOwn && onTip && (
          <button onClick={() => onTip({ userId: message.userId, userName: message.userName })}
            className="w-6 h-6 rounded bg-dark-700 hover:bg-dark-600 border border-dark-600 flex items-center justify-center text-gray-500 hover:text-emerald-400 transition-all"
            title="Send a tip">
            <DollarSign className="w-3.5 h-3.5" />
          </button>
        )}
        {!message.isSystem && onReply && (
          <button onClick={() => onReply({ messageId: message.messageId, userName: message.userName, content: message.content })}
            className="w-6 h-6 rounded bg-dark-700 hover:bg-dark-600 border border-dark-600 flex items-center justify-center text-gray-500 hover:text-accent-primary transition-all"
            title="Reply">
            <Reply className="w-3.5 h-3.5" />
          </button>
        )}

        {/* Quick reactions (top 6) + full picker */}
        <div className="flex gap-0.5 bg-dark-700 rounded-lg px-1 py-0.5 shadow-xl border border-dark-600 items-center">
          {REACTIONS.filter(r => !r.elite).slice(0, 6).map(r => (
            <button key={r.key} onClick={() => handleReact(r.key)} title={r.tip || r.label}
              className="w-6 h-6 rounded hover:bg-dark-600 flex items-center justify-center text-sm transition-all hover:scale-110">
              {r.emoji}
            </button>
          ))}
          {/* Full picker toggle */}
          <div className="relative">
            <button onClick={() => setShowModMenuOpen(prev => prev === 'reactions' ? false : 'reactions')}
              className="w-6 h-6 rounded hover:bg-dark-600 flex items-center justify-center text-xs text-gray-500 hover:text-white transition-all">
              +
            </button>
            {showModMenuOpen === 'reactions' && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowModMenuOpen(false)} />
                <div className="absolute right-0 top-8 z-50 bg-dark-800 border border-dark-600 rounded-xl shadow-2xl p-2.5 w-64">
                  {Object.entries(REACTION_CATS).map(([catKey, catInfo]) => {
                    const catReactions = REACTIONS.filter(r => r.cat === catKey && (!r.elite || message.userTier === 'elite'));
                    if (catReactions.length === 0) return null;
                    return (
                      <div key={catKey} className="mb-2 last:mb-0">
                        <p className={`text-[9px] font-bold uppercase tracking-wider mb-1 ${catInfo.color}`}>{catInfo.label}</p>
                        <div className="flex flex-wrap gap-0.5">
                          {catReactions.map(r => (
                            <button key={r.key} onClick={() => { handleReact(r.key); setShowModMenuOpen(false); }} title={r.tip || r.label}
                              className="w-8 h-8 rounded-lg hover:bg-dark-700 flex flex-col items-center justify-center transition-all hover:scale-110 group/r">
                              <span className="text-base">{r.emoji}</span>
                              <span className="text-[7px] text-gray-600 group-hover/r:text-gray-400 leading-none">{r.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Mod actions + self delete */}
        {showModMenu && (
          <div className="relative">
            <button onClick={() => setShowModMenuOpen(prev => prev === 'mod' ? false : 'mod')}
              className="w-6 h-6 rounded bg-dark-700 hover:bg-dark-600 border border-dark-600 flex items-center justify-center text-gray-500 hover:text-white transition-all">
              <MoreHorizontal className="w-3.5 h-3.5" />
            </button>
            {showModMenuOpen === 'mod' && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowModMenuOpen(false)} />
                <div className="absolute right-0 top-8 z-50 bg-dark-800 border border-dark-600 rounded-xl shadow-2xl py-1 w-44">
                  {/* Everyone can delete their own messages */}
                  {(canDeleteOwn || myPerms.del) && (
                    <button onClick={() => { onModAction('delete_message', { messageId: message.messageId, roomId: message.roomId }); setShowModMenuOpen(false); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-400 hover:text-red-400 hover:bg-dark-700 transition-all">
                      <Trash2 className="w-3.5 h-3.5" /> Delete Message
                    </button>
                  )}
                  {canModOthers && myPerms.warn && (
                    <button onClick={() => { onModAction('warn', { targetUserId: message.userId, roomId: message.roomId, reason: 'Community guidelines violation' }); setShowModMenuOpen(false); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-400 hover:text-amber-400 hover:bg-dark-700 transition-all">
                      <AlertTriangle className="w-3.5 h-3.5" /> Warn User
                    </button>
                  )}
                  {canModOthers && myPerms.mute && (
                    <>
                      {['1h', '6h', '24h', '7d'].map(dur => (
                        <button key={dur} onClick={() => { onModAction('mute', { targetUserId: message.userId, duration: dur, reason: 'Muted by moderator' }); setShowModMenuOpen(false); }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-400 hover:text-orange-400 hover:bg-dark-700 transition-all">
                          <VolumeX className="w-3.5 h-3.5" /> Mute {dur}
                        </button>
                      ))}
                    </>
                  )}
                  {canModOthers && myPerms.ban && (
                    <button onClick={() => { onModAction('ban', { targetUserId: message.userId, reason: 'Banned by administrator' }); setShowModMenuOpen(false); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-400 hover:bg-red-500/10 transition-all border-t border-dark-700 mt-1 pt-2">
                      <Ban className="w-3.5 h-3.5" /> Ban User
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── User Context Menu (click on username) ───────────────────────────────────

function UserContextMenu({ targetUser, myRole, onClose, onModAction, onAssignRole, isSelf }) {
  const [following, setFollowing] = useState(null); // null = loading, bool = known
  const [followBusy, setFollowBusy] = useState(false);

  useEffect(() => {
    if (isSelf || !targetUser?.userId || targetUser.userId === 'system') {
      setFollowing(false);
      return;
    }
    checkFollowing(targetUser.userId)
      .then(d => setFollowing(!!(d?.isFollowing || d?.following)))
      .catch(() => setFollowing(false));
  }, [targetUser?.userId, isSelf]);

  async function toggleFollow() {
    if (followBusy || isSelf) return;
    setFollowBusy(true);
    try {
      if (following) { await unfollowUser(targetUser.userId); setFollowing(false); }
      else            { await followUser(targetUser.userId);   setFollowing(true); }
    } catch {}
    setFollowBusy(false);
  }

  const myPerms = { owner: { del: true, warn: true, mute: true, ban: true, assign: true }, superadmin: { del: true, warn: true, mute: true, ban: true, assign: true }, admin: { del: true, warn: true, mute: true, ban: true, assign: true }, mod: { del: true, warn: true, mute: true, ban: false, assign: false } }[myRole] || {};
  const myLevel = { owner: 100, superadmin: 90, admin: 80, mod: 60, vip: 30, member: 10 }[myRole] || 10;
  const targetLevel = { owner: 100, superadmin: 90, admin: 80, mod: 60, vip: 30, member: 10 }[targetUser.chatRole] || 10;
  const canModTarget = !isSelf && myLevel > targetLevel;
  const assignableRoles = [];
  if (myPerms.assign && canModTarget) {
    const maxAssign = { owner: 90, superadmin: 80, admin: 60 }[myRole] || 0;
    if (maxAssign >= 90) assignableRoles.push({ id: 'superadmin', label: 'Super Admin' });
    if (maxAssign >= 80) assignableRoles.push({ id: 'admin', label: 'Admin' });
    if (maxAssign >= 60) assignableRoles.push({ id: 'mod', label: 'Moderator' });
    assignableRoles.push({ id: 'vip', label: 'VIP' }, { id: 'member', label: 'Member' });
  }

  const crb = CHAT_ROLE_BADGE[targetUser.chatRole];
  const tb = TIER_BADGE[targetUser.userTier] || TIER_BADGE.starter;

  return (
    <>
      <div className="fixed inset-0 z-50" onClick={onClose} />
      <div className="absolute z-50 bg-dark-800 border border-dark-600 rounded-xl shadow-2xl w-56 overflow-hidden animate-[slideUp_0.15s_ease]">
        <style>{`@keyframes slideUp { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }`}</style>
        {/* User info */}
        <div className="p-3 border-b border-dark-700">
          <div className="flex items-center gap-2.5">
            <UserAvatar name={targetUser.userName} chatRole={targetUser.chatRole} size="w-10 h-10" textSize="text-base" />
            <div>
              <p className="text-sm font-semibold text-white">{targetUser.userName}</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                {crb && <span className={`text-[8px] font-bold px-1.5 py-px rounded ${crb.bg} ${crb.text}`}>{crb.label}</span>}
                <span className={`text-[8px] font-bold px-1.5 py-px rounded ${tb.bg} ${tb.text} uppercase`}>{targetUser.userTier}</span>
              </div>
            </div>
          </div>
          {isSelf && <p className="text-[10px] text-gray-600 mt-1.5">This is your account</p>}
        </div>

        {/* Follow / Unfollow — only on other users */}
        {!isSelf && targetUser.userId !== 'system' && (
          <div className="py-1 border-b border-dark-700">
            <button onClick={toggleFollow} disabled={followBusy || following === null}
              className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold transition-all disabled:opacity-50 ${
                following
                  ? 'text-gray-400 hover:text-red-400 hover:bg-red-500/5'
                  : 'text-violet-300 hover:text-violet-200 hover:bg-violet-500/10'
              }`}>
              {followBusy || following === null
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : following
                  ? <UserMinus className="w-3.5 h-3.5" />
                  : <UserPlus className="w-3.5 h-3.5" />}
              {following === null ? 'Checking…' : following ? 'Unfollow' : 'Follow'}
            </button>
          </div>
        )}

        {/* View / Edit Profile link */}
        <div className="py-1 border-b border-dark-700">
          <a href={isSelf ? '/community/profile' : `/community/profile/${targetUser.userId}`}
            onClick={onClose}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-400 hover:text-white hover:bg-dark-700 transition-all">
            <Shield className="w-3.5 h-3.5" /> {isSelf ? 'Edit My Profile' : 'View Profile'}
          </a>
        </div>

        {/* Assign roles */}
        {assignableRoles.length > 0 && (
          <div className="py-1 border-b border-dark-700">
            <p className="px-3 py-1 text-[9px] text-gray-600 uppercase font-bold">Assign Role</p>
            {assignableRoles.map(r => (
              <button key={r.id} onClick={() => { onAssignRole(targetUser.userId, r.id); onClose(); }}
                className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs transition-all ${
                  targetUser.chatRole === r.id ? 'text-accent-primary bg-accent-primary/5' : 'text-gray-400 hover:text-white hover:bg-dark-700'
                }`}>
                <UserCog className="w-3 h-3" /> {r.label} {targetUser.chatRole === r.id ? '✓' : ''}
              </button>
            ))}
          </div>
        )}

        {/* Mod actions */}
        {canModTarget && (myPerms.warn || myPerms.mute || myPerms.ban) && (
          <div className="py-1">
            <p className="px-3 py-1 text-[9px] text-gray-600 uppercase font-bold">Moderation</p>
            {myPerms.warn && (
              <button onClick={() => { onModAction('warn', { targetUserId: targetUser.userId, reason: 'Community guidelines violation' }); onClose(); }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-400 hover:text-amber-400 hover:bg-dark-700">
                <AlertTriangle className="w-3 h-3" /> Warn
              </button>
            )}
            {myPerms.mute && ['1h', '6h', '24h', '7d'].map(d => (
              <button key={d} onClick={() => { onModAction('mute', { targetUserId: targetUser.userId, duration: d }); onClose(); }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-400 hover:text-orange-400 hover:bg-dark-700">
                <VolumeX className="w-3 h-3" /> Mute {d}
              </button>
            ))}
            {myPerms.ban && (
              <button onClick={() => { onModAction('ban', { targetUserId: targetUser.userId }); onClose(); }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-red-400 hover:bg-red-500/10 border-t border-dark-700 mt-1">
                <Ban className="w-3 h-3" /> Ban User
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// ─── Right Panel (Online Users) ──────────────────────────────────────────────

function OnlinePanel({ users, myRole, onModAction, onAssignRole }) {
  const [contextUser, setContextUser] = useState(null);
  const [contextPos, setContextPos] = useState({ x: 0, y: 0 });

  // Group by role category
  const staff = users.filter(u => ['owner', 'superadmin', 'admin', 'mod'].includes(u.chatRole));
  const vips = users.filter(u => u.chatRole === 'vip');
  const members = users.filter(u => !['owner', 'superadmin', 'admin', 'mod', 'vip'].includes(u.chatRole));

  function handleUserClick(e, user) {
    e.stopPropagation();
    setContextUser(user);
    setContextPos({ x: e.clientX, y: e.clientY });
  }

  function UserRow({ user }) {
    const crb = CHAT_ROLE_BADGE[user.chatRole];
    return (
      <button onClick={(e) => handleUserClick(e, user)}
        className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-dark-700 transition-all text-left">
        <div className="relative flex-shrink-0">
          <UserAvatar name={user.userName} chatRole={user.chatRole} avatar={user.avatar} size="w-7 h-7" textSize="text-[10px]" />
          <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-dark-800 ${
            user.online ? 'bg-emerald-400' : 'bg-gray-600'
          }`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-xs truncate ${user.online ? 'text-white' : 'text-gray-500'}`}>{user.userName}</p>
        </div>
        {crb && <crb.icon className={`w-3 h-3 flex-shrink-0 ${crb.text}`} />}
      </button>
    );
  }

  function Section({ label, color, items }) {
    if (!items.length) return null;
    return (
      <div className="mb-3">
        <p className={`text-[9px] font-bold uppercase tracking-widest px-3 py-1 ${color}`}>
          {label} — {items.length}
        </p>
        {items.map(u => <UserRow key={u.userId} user={u} />)}
      </div>
    );
  }

  return (
    <aside className="w-52 bg-dark-800 border-l border-dark-700/50 flex-shrink-0 overflow-y-auto hidden xl:block">
      <div className="p-3 border-b border-dark-700/50">
        <h4 className="text-xs font-bold text-gray-400 flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5" /> Users — {users.filter(u => u.online).length} online
        </h4>
      </div>
      <div className="py-2 px-1">
        <Section label="Staff" color="text-amber-400" items={staff} />
        <Section label="VIP" color="text-violet-400" items={vips} />
        <Section label="Members" color="text-gray-500" items={members} />
      </div>

      {/* Context menu */}
      {contextUser && (
        <div style={{ position: 'fixed', left: Math.min(contextPos.x, window.innerWidth - 240), top: Math.min(contextPos.y, window.innerHeight - 300) }}>
          <UserContextMenu
            targetUser={contextUser}
            myRole={myRole}
            onClose={() => setContextUser(null)}
            onModAction={onModAction}
            onAssignRole={onAssignRole}
          />
        </div>
      )}
    </aside>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function CommunityPage() {
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState('general');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showRooms, setShowRooms] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [error, setError] = useState(null);
  const [myRole, setMyRole] = useState('member');
  const [toast, setToast] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [pendingImage, setPendingImage] = useState(null); // base64 data URL
  const [replyTo, setReplyTo] = useState(null); // { messageId, userName, content }
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [guidelinesRequired, setGuidelinesRequired] = useState(false);
  const [showBannedUsers, setShowBannedUsers] = useState(false);
  const [lockedRoom, setLockedRoom] = useState(null);
  const [showPollModal, setShowPollModal] = useState(false);
  const [tipTarget, setTipTarget] = useState(null); // { userId, userName }
  const [showMonetize, setShowMonetize] = useState(false);
  const [showSubscribe, setShowSubscribe] = useState(false);
  const [showShareTrade, setShowShareTrade] = useState(false);
  const paidChannelStatus = usePaidChannelStatus();
  const { apps: myPaidApplications, refresh: refreshMyApplications } = useMyPaidApplications();
  const [roomPolls, setRoomPolls] = useState([]);
  const [topCallsThisWeek, setTopCallsThisWeek] = useState([]);
  const [topCallsExpanded, setTopCallsExpanded] = useState(false);
  const [roomSummary, setRoomSummary] = useState(null);
  const [summaryExpanded, setSummaryExpanded] = useState(false); // room obj for upgrade modal
  const [activeAlert, setActiveAlert] = useState(null);
  const imageInputRef = useRef(null);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);
  const [typingTotal, setTypingTotal] = useState(0); // total typers — may be > typingUsers.length at scale
  const [roomActivityRate, setRoomActivityRate] = useState(0); // messages/min in last 60s
  const lastTypingSentRef = useRef(0);
  const [allReactions, setAllReactions] = useState({});
  const avatarCache = useRef({}); // Persists avatars across room switches
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const pollRef = useRef(null);
  const inputRef = useRef(null);
  const userScrolledUp = useRef(false);
  const justSent = useRef(false);

  // Load rooms + role
  const loadRooms = useCallback(async () => {
    try {
      const d = await getChatRooms();
      setRooms(d?.rooms || []);
    } catch {}
  }, []);

  useEffect(() => { loadRooms(); }, [loadRooms]);

  // Check if user has acknowledged guidelines on first visit
  useEffect(() => {
    getGuidelinesStatus()
      .then(d => { if (!d?.acknowledged) setGuidelinesRequired(true); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    getChatRolesList().then(d => { if (d?.myRole) setMyRole(d.myRole); }).catch(() => {});
  }, []);

  // Heartbeat + online users
  useEffect(() => {
    // Send heartbeat immediately, then every 30s
    chatHeartbeat(activeRoom).catch(() => {});
    const hbInterval = setInterval(() => chatHeartbeat(activeRoom).catch(() => {}), 30000);

    // Load online users immediately, then every 10s
    const loadOnline = () => getChatOnlineUsers(activeRoom).then(d => {
      const users = d?.users || [];
      // Cache avatars from online users so they persist across room switches
      users.forEach(u => { if (u.avatar) avatarCache.current[u.userId] = u.avatar; });
      setOnlineUsers(users);
      // Typing indicator (filter out self). typingTotal counts ALL typers (may
      // exceed array length at scale because backend caps the array at 5).
      const others = (d?.typing || []).filter(t => t.userId !== user?.userId);
      const totalFromServer = d?.typingTotal != null ? d.typingTotal : others.length + (d?.typing?.some(t => t.userId === user?.userId) ? 1 : 0);
      const totalOthers = totalFromServer - (d?.typing?.some(t => t.userId === user?.userId) ? 1 : 0);
      setTypingUsers(others);
      setTypingTotal(Math.max(0, totalOthers));
      setRoomActivityRate(d?.messageRate60s || 0);
    }).catch(() => {});
    setTimeout(loadOnline, 2000); // 2s delay for heartbeat to register
    const onlineInterval = setInterval(loadOnline, 10000);

    return () => { clearInterval(hbInterval); clearInterval(onlineInterval); };
  }, [activeRoom]);

  // Load reactions for active room
  const loadReactions = useCallback(async () => {
    try {
      const data = await getChatReactions(activeRoom);
      setAllReactions(data?.reactions || {});
    } catch {}
  }, [activeRoom]);

  // Load messages + poll
  const loadMessages = useCallback(async () => {
    try {
      const data = await getChatMessages(activeRoom, 50);
      const msgs = data?.messages || [];
      // Cache avatars from messages
      msgs.forEach(m => { if (m.avatar) avatarCache.current[m.userId] = m.avatar; });
      setMessages(msgs);
      setError(null);
    } catch (err) {
      // 403 SUBSCRIPTION_REQUIRED is a normal state for paid channels — don't show error banner
      const isPaidGate = String(err?.code || err?.message || '').toUpperCase().includes('SUBSCRIPTION');
      if (!messages.length && !isPaidGate) setError('Failed to load messages');
      if (isPaidGate) setMessages([]);
    }
    setLoading(false);
  }, [activeRoom]);

  useEffect(() => {
    setLoading(true);
    setMessages([]);
    setAllReactions({});
    setRoomPolls([]);
    setRoomSummary(null);
    setSummaryExpanded(false);

    // Safety net: never trap the user on the spinner. After 6s, render the
    // empty/cached state regardless of whether the room fetch has settled.
    // The polling interval below will continue to refresh in the background.
    const safetyTimer = setTimeout(() => setLoading(false), 6000);

    loadMessages();
    loadReactions();
    getRoomPolls(activeRoom).then(d => setRoomPolls(d?.items || [])).catch(() => {});
    getRoomSummary(activeRoom).then(d => { if (d && !d.empty) setRoomSummary(d); }).catch(() => {});
    getTopCallsThisWeek(activeRoom).then(d => setTopCallsThisWeek(d?.calls || [])).catch(() => setTopCallsThisWeek([]));
    markChatRead(activeRoom).catch(() => {});
    pollRef.current = setInterval(() => {
      loadMessages();
      loadReactions();
      markChatRead(activeRoom).catch(() => {});
    }, 5000);
    return () => {
      clearInterval(pollRef.current);
      clearTimeout(safetyTimer);
    };
  }, [activeRoom, loadMessages, loadReactions]);

  // Refresh room list periodically for unread counts
  useEffect(() => {
    const roomPoll = setInterval(loadRooms, 15000);
    return () => clearInterval(roomPoll);
  }, [loadRooms]);

  // Poll notifications
  useEffect(() => {
    const loadNotifs = () => getChatNotifications().then(d => {
      setNotifications(d?.notifications || []);
      setUnreadNotifCount(d?.unreadCount || 0);
    }).catch(() => {});
    loadNotifs();
    const notifPoll = setInterval(loadNotifs, 15000);
    return () => clearInterval(notifPoll);
  }, []);

  // Poll for moderation alerts (warnings, mutes, bans)
  useEffect(() => {
    const checkAlerts = () => {
      getChatAlerts().then(d => {
        const pending = d?.alerts || [];
        if (pending.length > 0 && !activeAlert) {
          setActiveAlert(pending[0]); // Show one at a time
        }
      }).catch(() => {});
    };
    checkAlerts();
    const alertPoll = setInterval(checkAlerts, 10000);
    return () => clearInterval(alertPoll);
  }, [activeAlert]);

  async function handleAcknowledgeAlert(alertId) {
    try {
      await acknowledgeChatAlert(alertId);
      setActiveAlert(null);
    } catch {}
  }

  // Only auto-scroll if user is at the bottom or just sent a message
  useEffect(() => {
    if (!userScrolledUp.current || justSent.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: justSent.current ? 'smooth' : 'auto' });
      justSent.current = false;
    }
  }, [messages]);

  // Track if user has scrolled up
  function handleScroll() {
    const el = messagesContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    userScrolledUp.current = distanceFromBottom > 100;
  }

  async function handleSend() {
    const text = input.trim();
    const imageToSend = pendingImage;
    if ((!text && !imageToSend) || sending) return;

    // /poll slash command — `/poll Question? | Yes | No | Maybe`
    if (text.startsWith('/poll ')) {
      const body = text.slice(6).trim();
      const parts = body.split('|').map(s => s.trim()).filter(Boolean);
      if (parts.length < 3) {
        setError('Poll format: /poll Question? | Option 1 | Option 2 (at least 2 options)');
        return;
      }
      const question = parts[0];
      const options = parts.slice(1, 5); // max 4 options
      setInput('');
      setSending(true);
      try {
        await createPoll(activeRoom, question, options);
        await getRoomPolls(activeRoom).then(d => setRoomPolls(d?.items || []));
        setToast('Poll posted');
        setTimeout(() => setToast(null), 2500);
      } catch (err) {
        setError(err.message);
      }
      setSending(false);
      setTimeout(() => inputRef.current?.focus(), 50);
      return;
    }

    const replyToId = replyTo?.messageId || null;
    setInput('');
    setPendingImage(null);
    setReplyTo(null);
    setShowMentions(false);
    setSending(true);
    justSent.current = true;
    userScrolledUp.current = false;
    try {
      await sendChatMessage(activeRoom, text || '', replyToId, imageToSend || undefined);
      await loadMessages();
    } catch (err) {
      setError(err.message);
    }
    setSending(false);
    setTimeout(() => inputRef.current?.focus(), 50);
  }

  function handleImageSelect(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5_000_000) { setToast('Image too large — max 5MB'); setTimeout(() => setToast(null), 3000); return; }
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      // Resize to max 800px wide, preserve aspect ratio
      const maxW = 800;
      const scale = img.width > maxW ? maxW / img.width : 1;
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
      setPendingImage(dataUrl);
    };
    img.src = URL.createObjectURL(file);
    e.target.value = ''; // Reset so same file can be selected again
  }

  async function handleAssignRole(targetUserId, role) {
    try {
      await assignChatRole(targetUserId, role);
      setToast(`Role assigned: ${role}`);
      setTimeout(() => setToast(null), 3000);
    } catch (err) {
      setToast(`Failed: ${err.message}`);
      setTimeout(() => setToast(null), 4000);
    }
  }

  async function handleCreateChannel(name, description, icon) {
    await createChatChannel(name, description, icon);
    await loadRooms();
  }

  async function handleReactToMessage(messageId, reaction) {
    try {
      // Optimistic toggle
      setAllReactions(prev => {
        const msgR = { ...(prev[messageId] || {}) };
        const existing = msgR[reaction] || { count: 0, mine: false };
        if (existing.mine) {
          // Remove my reaction
          const newCount = Math.max(0, existing.count - 1);
          if (newCount === 0) { delete msgR[reaction]; } else { msgR[reaction] = { count: newCount, mine: false }; }
        } else {
          // Add my reaction
          msgR[reaction] = { count: (existing.count || 0) + 1, mine: true };
        }
        return { ...prev, [messageId]: msgR };
      });
      await reactToMessage(messageId, activeRoom, reaction);
    } catch {}
  }

  async function handleModAction(action, params) {
    try {
      await moderateChat(action, params);
      const labels = { delete_message: 'Message deleted', warn: 'User warned', mute: 'User muted', ban: 'User banned', unmute: 'User unmuted', unban: 'User unbanned' };
      setToast(labels[action] || 'Action completed');
      setTimeout(() => setToast(null), 3000);
      await loadMessages();
    } catch (err) {
      setToast(`Failed: ${err.message}`);
      setTimeout(() => setToast(null), 4000);
    }
  }

  const activeRoomInfo = rooms.find(r => r.id === activeRoom) || { name: 'General', icon: '💬', description: '' };
  const postRoleLevels = { owner: 100, superadmin: 90, admin: 80, mod: 60, vip: 30, member: 10 };
  const isPaidLocked = activeRoomInfo.isPaid && activeRoomInfo.accessible === false;
  const canPost = !isPaidLocked && (!activeRoomInfo.postRole || (postRoleLevels[myRole] || 10) >= (postRoleLevels[activeRoomInfo.postRole] || 80));

  return (
    <div className="flex animate-fade-in -mx-4 -mb-4 lg:-mx-6 lg:-mb-6 overflow-hidden" style={{ height: 'calc(100vh - 11rem)' }}>
      {/* Room sidebar */}
      <RoomList
        rooms={rooms}
        activeRoom={activeRoom}
        onSelect={setActiveRoom}
        onCreateChannel={() => setShowCreateModal(true)}
        show={showRooms}
        onClose={() => setShowRooms(false)}
        onLockedRoom={setLockedRoom}
      />

      {/* Chat area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-dark-700/50 flex-shrink-0">
          <button onClick={() => setShowRooms(true)} className="lg:hidden p-1.5 text-gray-500 hover:text-white">
            <Hash className="w-5 h-5" />
          </button>
          <span className="text-lg">{activeRoomInfo.icon}</span>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-bold text-white">{activeRoomInfo.name}</h2>
            <p className="text-[10px] text-gray-500 truncate">{activeRoomInfo.description || 'Community chat'}</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Create poll/prediction */}
            {/* Admin: seed content */}
            {(myRole === 'owner' || myRole === 'admin' || myRole === 'superadmin') && (
              <button onClick={async () => {
                try {
                  await generateRoomContent(activeRoom);
                  setToast('🤖 Bot content posted to this room');
                  setTimeout(() => setToast(null), 3000);
                  await loadMessages();
                } catch (err) {
                  setToast(err.message || 'No content generator for this room');
                  setTimeout(() => setToast(null), 3000);
                }
              }} className="p-1.5 text-gray-600 hover:text-emerald-400 transition-colors" title="Generate AI content for this room">
                <Rocket className="w-4 h-4" />
              </button>
            )}
            <button onClick={() => setShowPollModal(true)} className="p-1.5 text-gray-600 hover:text-accent-primary transition-colors" title="Create Poll or Prediction">
              <BarChart3 className="w-4 h-4" />
            </button>
            {/* Paid channel state — three states: not applied / pending / approved */}
            {paidChannelStatus.enabled && activeRoomInfo.isCustom && activeRoomInfo.isOwner && !activeRoomInfo.isPaid && (() => {
              const app = myPaidApplications[activeRoom];
              if (app?.status === 'pending_review') {
                return (
                  <span className="px-2 py-1 rounded-lg bg-amber-500/15 border border-amber-500/30 text-amber-400 text-[10px] font-bold flex items-center gap-1" title="Your application is awaiting admin review">
                    <Loader2 className="w-3 h-3 animate-spin" /> Pending Review
                  </span>
                );
              }
              if (app?.status === 'rejected') {
                return (
                  <button onClick={() => setShowMonetize(true)} className="px-2 py-1 rounded-lg bg-red-500/15 border border-red-500/30 text-red-400 hover:bg-red-500/25 transition-colors text-[10px] font-bold flex items-center gap-1" title={app.reviewNotes || 'Application was rejected — reapply'}>
                    <AlertTriangle className="w-3 h-3" /> Reapply
                  </button>
                );
              }
              return (
                <button onClick={() => setShowMonetize(true)} className="px-2 py-1 rounded-lg bg-amber-500/15 border border-amber-500/30 text-amber-400 hover:bg-amber-500/25 transition-colors text-[10px] font-bold flex items-center gap-1" title="Apply to charge subscribers — admin approval required">
                  <DollarSign className="w-3 h-3" /> Monetize
                </button>
              );
            })()}
            {/* Approved + active premium badge */}
            {paidChannelStatus.enabled && activeRoomInfo.isPaid && (
              <span className="px-2 py-1 rounded-lg bg-amber-500/15 border border-amber-500/30 text-amber-400 text-[10px] font-bold flex items-center gap-1">
                <Crown className="w-3 h-3" /> Premium
              </span>
            )}
            {/* Notification bell */}
            <div className="relative">
              <button onClick={() => setShowNotifications(!showNotifications)} className="p-1.5 text-gray-600 hover:text-accent-primary transition-colors relative" title="Notifications">
                <Bell className="w-4 h-4" />
                {unreadNotifCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] rounded-full bg-red-500 text-white text-[8px] font-bold flex items-center justify-center px-0.5">
                    {unreadNotifCount > 9 ? '9+' : unreadNotifCount}
                  </span>
                )}
              </button>
              {showNotifications && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowNotifications(false)} />
                  <div className="absolute right-0 top-8 z-50 bg-dark-800 border border-dark-700/50 rounded-xl shadow-2xl w-80 max-h-96 overflow-y-auto">
                    <div className="sticky top-0 bg-dark-800 border-b border-dark-700/50 px-4 py-2.5 flex items-center justify-between">
                      <h4 className="text-xs font-bold text-white">Notifications</h4>
                      {unreadNotifCount > 0 && (
                        <button onClick={async () => { await markNotificationsRead(null, true); setNotifications(prev => prev.map(n => ({...n, read: true}))); setUnreadNotifCount(0); }}
                          className="text-[10px] text-accent-primary hover:text-accent-primary/80">Mark all read</button>
                      )}
                    </div>
                    {notifications.length === 0 ? (
                      <p className="text-xs text-gray-500 text-center py-6">No notifications yet</p>
                    ) : (
                      notifications.slice(0, 30).map(n => (
                        <button key={n.id} onClick={async () => {
                          if (!n.read) { await markNotificationsRead(n.id); setNotifications(prev => prev.map(x => x.id === n.id ? {...x, read: true} : x)); setUnreadNotifCount(prev => Math.max(0, prev - 1)); }
                          if (n.roomId && n.roomId !== activeRoom) setActiveRoom(n.roomId);
                          setShowNotifications(false);
                        }}
                          className={`w-full flex items-start gap-2.5 px-4 py-3 text-left hover:bg-dark-700 transition-colors border-b border-dark-700/30 ${!n.read ? 'bg-accent-primary/5' : ''}`}>
                          <span className="text-base flex-shrink-0 mt-0.5">{
                            n.type === 'mention' ? '💬' :
                            n.type === 'reply' ? '↩️' :
                            n.type === 'tip' ? '💰' :
                            n.type === 'tip_read' ? '✓' : '🔔'
                          }</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-white">
                              <span className="font-semibold">{n.fromUserName}</span>
                              {n.type === 'mention' ? ' mentioned you' :
                               n.type === 'reply' ? ' replied to you' :
                               n.type === 'tip' ? ` sent a $${n.tipAmount} tip` :
                               n.type === 'tip_read' ? ` saw your $${n.tipAmount} tip` : ''}
                            </p>
                            <p className="text-[10px] text-gray-500 truncate mt-0.5">{n.preview}</p>
                            <p className="text-[9px] text-gray-600 mt-0.5">{timeAgo(n.timestamp)}</p>
                          </div>
                          {!n.read && <span className="w-2 h-2 rounded-full bg-accent-primary flex-shrink-0 mt-1.5" />}
                        </button>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
            <button onClick={() => setShowGuidelines(true)} className="p-1.5 text-gray-600 hover:text-accent-primary transition-colors" title="Community Guidelines">
              <BookOpen className="w-4 h-4" />
            </button>
            {(myRole === 'owner' || myRole === 'admin' || myRole === 'superadmin') && (
              <button onClick={() => setShowBannedUsers(true)} className="p-1.5 text-gray-600 hover:text-red-400 transition-colors" title="Banned Users">
                <UserX className="w-4 h-4" />
              </button>
            )}
            {(myRole === 'owner' || myRole === 'admin' || myRole === 'superadmin') && (
              <button onClick={async () => {
                try {
                  await moderateChat('clear_system_messages', { roomId: activeRoom });
                  setToast('System messages cleared');
                  setTimeout(() => setToast(null), 3000);
                  await loadMessages();
                } catch { setToast('Failed to clear'); setTimeout(() => setToast(null), 3000); }
              }} className="p-1.5 text-gray-600 hover:text-amber-400 transition-colors" title="Clear system messages">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            <button onClick={loadMessages} className="p-1.5 text-gray-600 hover:text-white transition-colors" title="Refresh">
              <RefreshCw className="w-4 h-4" />
            </button>
            {CHAT_ROLE_BADGE[myRole] && (
              <span className={`text-[9px] font-bold px-2 py-1 rounded-lg flex items-center gap-1 ${CHAT_ROLE_BADGE[myRole].bg} ${CHAT_ROLE_BADGE[myRole].text}`}>
                {CHAT_ROLE_BADGE[myRole].label}
              </span>
            )}
            {roomActivityRate >= 10 && (
              <span className="text-[10px] font-bold px-2 py-1 rounded-lg flex items-center gap-1 bg-orange-500/20 border border-orange-500/30 text-orange-400" title={`${roomActivityRate} messages in the last minute — this room is buzzing`}>
                <Flame className="w-3 h-3" /> HOT · {roomActivityRate}/min
              </span>
            )}
            <span className="text-[10px] text-gray-600 bg-dark-700 px-2 py-1 rounded-lg flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Live
            </span>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto" ref={messagesContainerRef} onScroll={handleScroll}>
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-6 h-6 text-accent-primary animate-spin" />
            </div>
          ) : (activeRoomInfo.isPaid && activeRoomInfo.accessible === false) ? (
            <PaidChannelLockedPanel room={activeRoomInfo} onSubscribe={() => setShowSubscribe(true)} />
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-6">
              <span className="text-4xl mb-3">{activeRoomInfo.icon}</span>
              <h3 className="text-white font-semibold mb-1">Welcome to #{activeRoomInfo.name}</h3>
              <p className="text-gray-500 text-sm max-w-sm">
                {activeRoomInfo.description || 'Start a conversation with the community.'}
              </p>
              <p className="text-[10px] text-gray-600 mt-3">Be the first to send a message</p>
            </div>
          ) : (
            <div className="py-2">
              {/* Room summary */}
              {roomSummary && (
                <div className="px-4 py-2 border-b border-dark-700/30">
                  <button onClick={() => setSummaryExpanded(!summaryExpanded)}
                    className="w-full bg-gradient-to-r from-accent-primary/5 to-violet-500/5 rounded-xl p-3 border border-accent-primary/15 text-left hover:border-accent-primary/25 transition-all">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-base">🧠</span>
                        <span className="text-xs font-bold text-accent-primary">haythix AI Summary</span>
                        <span className="text-[9px] text-gray-500">{roomSummary.messageCount} msgs · {roomSummary.userCount} users · last 6h</span>
                      </div>
                      <span className="text-[10px] text-gray-500">{summaryExpanded ? '▼' : '▶'}</span>
                    </div>
                    {summaryExpanded && (
                      <div className="mt-2 text-xs text-gray-300 leading-relaxed whitespace-pre-line border-t border-dark-700/30 pt-2">
                        {roomSummary.summary}
                      </div>
                    )}
                  </button>
                </div>
              )}

              {/* Top Calls of the Week */}
              {topCallsThisWeek.length > 0 && (
                <div className="px-4 py-2 border-b border-dark-700/30">
                  <button onClick={() => setTopCallsExpanded(!topCallsExpanded)}
                    className="w-full bg-gradient-to-r from-amber-500/5 to-orange-500/5 rounded-xl p-3 border border-amber-500/20 text-left hover:border-amber-500/30 transition-all">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-base">🏆</span>
                        <span className="text-xs font-bold text-amber-400">Top Calls This Week</span>
                        <span className="text-[9px] text-gray-500">{topCallsThisWeek.length} pinned · auto-ranked by % gain</span>
                      </div>
                      <span className="text-[10px] text-gray-500">{topCallsExpanded ? '▼' : '▶'}</span>
                    </div>
                    {topCallsExpanded && (
                      <div className="mt-3 space-y-2 border-t border-dark-700/30 pt-2">
                        {topCallsThisWeek.map((c, i) => (
                          <div key={c.callId} className="flex items-center gap-2 bg-dark-800/40 rounded-lg px-2 py-1.5">
                            <span className="text-[10px] text-gray-500 font-mono w-4">#{i + 1}</span>
                            <span className="text-base">{i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉'}</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-white truncate">
                                <span className="font-bold">{c.userName}</span>
                                <span className="text-gray-500"> · {c.action === 'bullish' ? '🟢 LONG' : '🔴 SHORT'} </span>
                                <span className="text-accent-primary">${c.token}</span>
                              </p>
                              <p className="text-[9px] text-gray-600">{new Date(c.callTimestamp).toLocaleDateString()}</p>
                            </div>
                            <span className={`text-sm font-bold ${c.bestPct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                              {c.bestPct >= 0 ? '+' : ''}{c.bestPct}%
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </button>
                </div>
              )}

              {/* Active polls & predictions */}
              {roomPolls.filter(p => !p.resolvedAt).length > 0 && (
                <div className="px-4 py-2 space-y-2 border-b border-dark-700/30 mb-2">
                  {roomPolls.filter(p => !p.resolvedAt).map(item => (
                    item.type === 'poll' ? (
                      <div key={item.pollId} className="bg-dark-700/40 rounded-xl p-4 border border-dark-700/50">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-base">📊</span>
                          <span className="text-xs font-semibold text-white">{item.creatorName}</span>
                          <span className="text-[9px] text-gray-500">asked</span>
                        </div>
                        <p className="text-sm font-semibold text-white mb-3">{item.question}</p>
                        <div className="space-y-1.5">
                          {item.options.map((opt, i) => {
                            const votes = item.votes[`option-${i}`] || [];
                            const total = item.totalVotes || 1;
                            const pct = Math.round((votes.length / total) * 100) || 0;
                            const myVote = votes.includes(user?.userId);
                            return (
                              <button key={i} onClick={async () => { const r = await votePoll(item.pollId, i); setRoomPolls(prev => prev.map(p => p.pollId === item.pollId ? r : p)); }}
                                className={`w-full relative rounded-lg px-3 py-2 text-left text-sm transition-all overflow-hidden ${
                                  myVote ? 'border border-accent-primary/30' : 'border border-dark-600 hover:border-dark-500'
                                }`}>
                                <div className={`absolute inset-y-0 left-0 ${myVote ? 'bg-accent-primary/15' : 'bg-dark-600/50'} transition-all`} style={{ width: `${pct}%` }} />
                                <div className="relative flex items-center justify-between">
                                  <span className={myVote ? 'text-accent-primary font-medium' : 'text-gray-300'}>{opt}</span>
                                  <span className="text-xs text-gray-500">{votes.length} ({pct}%)</span>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          <p className="text-[9px] text-gray-600">{item.totalVotes || 0} votes</p>
                          {(item.creatorId === user?.userId || myRole === 'owner' || myRole === 'admin' || myRole === 'superadmin') && (
                            <button onClick={async () => { await deletePollOrPrediction(item.pollId, null); setRoomPolls(prev => prev.filter(p => p.pollId !== item.pollId)); }}
                              className="text-[9px] text-gray-600 hover:text-red-400 transition-colors">Remove</button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div key={item.predictionId} className="bg-dark-700/40 rounded-xl p-4 border border-dark-700/50">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-base">🔮</span>
                          <span className="text-xs font-semibold text-white">{item.creatorName}</span>
                          <span className="text-[9px] text-gray-500">predicts</span>
                          <span className="text-[8px] font-bold px-1.5 py-px rounded bg-violet-500/15 text-violet-400 ml-auto">PREDICTION</span>
                        </div>
                        <p className="text-sm font-semibold text-white mb-1">
                          ${item.asset} will be {item.predictionType === 'price_above' ? 'ABOVE' : 'BELOW'} <span className="text-accent-primary">${item.targetPrice?.toLocaleString()}</span>
                        </p>
                        <p className="text-[10px] text-gray-500 mb-3">
                          Started at ${item.startPrice?.toLocaleString()} · Resolves {new Date(item.resolveAt).toLocaleDateString()}
                        </p>
                        <div className="flex gap-2">
                          <button onClick={async () => { const r = await votePrediction(item.predictionId, 'agree'); setRoomPolls(prev => prev.map(p => p.predictionId === item.predictionId ? r : p)); }}
                            className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all ${
                              item.votes?.agree?.includes(user?.userId) ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-dark-600 text-gray-400 hover:bg-dark-500'
                            }`}>
                            ✓ Agree ({item.votes?.agree?.length || 0})
                          </button>
                          <button onClick={async () => { const r = await votePrediction(item.predictionId, 'disagree'); setRoomPolls(prev => prev.map(p => p.predictionId === item.predictionId ? r : p)); }}
                            className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all ${
                              item.votes?.disagree?.includes(user?.userId) ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-dark-600 text-gray-400 hover:bg-dark-500'
                            }`}>
                            ✗ Disagree ({item.votes?.disagree?.length || 0})
                          </button>
                        </div>
                        {(item.creatorId === user?.userId || myRole === 'owner' || myRole === 'admin' || myRole === 'superadmin') && (
                          <div className="mt-2 text-right">
                            <button onClick={async () => { await deletePollOrPrediction(null, item.predictionId); setRoomPolls(prev => prev.filter(p => p.predictionId !== item.predictionId)); }}
                              className="text-[9px] text-gray-600 hover:text-red-400 transition-colors">Remove</button>
                          </div>
                        )}
                      </div>
                    )
                  ))}
                </div>
              )}

              {messages.map(msg => (
                <MessageBubble key={msg.messageId} message={{...msg, avatar: msg.avatar || onlineUsers.find(u => u.userId === msg.userId)?.avatar || avatarCache.current[msg.userId] || null, streak: msg.streak || onlineUsers.find(u => u.userId === msg.userId)?.streak || 0}} isOwn={msg.userId === user?.userId} myRole={myRole} onModAction={handleModAction} onAssignRole={handleAssignRole} reactions={allReactions[msg.messageId] || {}} onReact={handleReactToMessage} onReply={setReplyTo} onTip={setTipTarget} allMessages={messages} />
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="px-4 py-2 bg-red-500/10 border-t border-red-500/20">
            <p className="text-xs text-red-400 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" /> {error}
            </p>
          </div>
        )}

        {/* Input */}
        <div className="border-t border-dark-700/50 p-4 flex-shrink-0 relative">
          {canPost ? (
            <>
              {/* Typing indicator — collapses gracefully at scale */}
              {typingTotal > 0 && (
                <div className="px-4 py-1 flex items-center gap-1.5 text-[10px] text-gray-500">
                  <span className="flex gap-0.5">
                    <span className="w-1 h-1 rounded-full bg-accent-primary animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1 h-1 rounded-full bg-accent-primary animate-bounce" style={{ animationDelay: '120ms' }} />
                    <span className="w-1 h-1 rounded-full bg-accent-primary animate-bounce" style={{ animationDelay: '240ms' }} />
                  </span>
                  <span>
                    {typingTotal === 1 && typingUsers[0] ? `${typingUsers[0].userName} is typing…` :
                     typingTotal === 2 && typingUsers[0] && typingUsers[1] ? `${typingUsers[0].userName} and ${typingUsers[1].userName} are typing…` :
                     typingTotal <= 5 && typingUsers[0] ? `${typingUsers[0].userName} and ${typingTotal - 1} others are typing…` :
                     `${typingTotal} people are typing…`}
                  </span>
                </div>
              )}

              {/* Reply context bar */}
              {replyTo && (
                <div className="flex items-center gap-2 bg-dark-800/80 border border-accent-primary/20 rounded-t-xl px-4 py-2 -mb-1">
                  <Reply className="w-3.5 h-3.5 text-accent-primary flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-[10px] text-accent-primary font-medium">Replying to {replyTo.userName}</span>
                    <p className="text-xs text-gray-500 truncate">{replyTo.content?.slice(0, 80)}</p>
                  </div>
                  <button onClick={() => setReplyTo(null)} className="p-1 text-gray-500 hover:text-white">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}

              {/* @mention autocomplete */}
              {showMentions && mentionQuery && (
                <div className="absolute bottom-20 left-4 z-50 bg-dark-800 border border-dark-700/50 rounded-xl shadow-2xl py-1 w-64">
                  {onlineUsers
                    .filter(u => u.userName?.toLowerCase().includes(mentionQuery.toLowerCase()) && u.userId !== user?.userId)
                    .slice(0, 6)
                    .map(u => (
                      <button key={u.userId} onClick={() => {
                        const before = input.slice(0, input.lastIndexOf('@'));
                        setInput(before + '@' + u.userName + ' ');
                        setShowMentions(false);
                        setMentionQuery('');
                        setTimeout(() => inputRef.current?.focus(), 50);
                      }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-dark-700 transition-colors">
                        <UserAvatar name={u.userName} chatRole={u.chatRole} avatar={u.avatar} size="w-7 h-7" textSize="text-xs" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white truncate">{u.userName}</p>
                          <p className="text-[9px] text-gray-500">{u.chatRole} · {u.userTier}</p>
                        </div>
                      </button>
                    ))
                  }
                  {onlineUsers.filter(u => u.userName?.toLowerCase().includes(mentionQuery.toLowerCase()) && u.userId !== user?.userId).length === 0 && (
                    <p className="text-xs text-gray-500 px-3 py-2">No users found</p>
                  )}
                </div>
              )}

              {/* Emoji picker popup */}
              {showEmojiPicker && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowEmojiPicker(false)} />
                  <div className="absolute bottom-20 left-4 z-50 bg-dark-800 border border-dark-700/50 rounded-2xl shadow-2xl p-3 w-72">
                    {Object.entries(EMOJI_CATEGORIES).map(([cat, emojis]) => (
                      <div key={cat} className="mb-2 last:mb-0">
                        <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">{cat}</p>
                        <div className="flex flex-wrap gap-0.5">
                          {emojis.map(e => (
                            <button key={e} onClick={() => { setInput(prev => prev + e); setShowEmojiPicker(false); setTimeout(() => inputRef.current?.focus(), 50); }}
                              className="w-8 h-8 rounded-lg hover:bg-dark-700 flex items-center justify-center text-lg transition-all hover:scale-110">
                              {e}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Image preview */}
              {pendingImage && (
                <div className="mb-2 relative inline-block">
                  <img src={pendingImage} alt="Upload preview" className="max-h-32 rounded-xl border border-dark-700/50" />
                  <button onClick={() => setPendingImage(null)}
                    className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center text-xs hover:bg-red-400 transition-all shadow-lg">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}

              <div className="flex items-end gap-2 bg-dark-800 border border-dark-700/50 rounded-2xl px-4 py-3
                focus-within:border-accent-primary/40 transition-colors">
                <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageSelect} />
                <button onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  className="p-1.5 rounded-lg text-gray-500 hover:text-accent-primary hover:bg-dark-700 transition-all flex-shrink-0">
                  <Smile className="w-5 h-5" />
                </button>
                <button onClick={() => imageInputRef.current?.click()}
                  className="p-1.5 rounded-lg text-gray-500 hover:text-accent-primary hover:bg-dark-700 transition-all flex-shrink-0">
                  <ImagePlus className="w-5 h-5" />
                </button>
                <button onClick={() => setShowShareTrade(true)}
                  title="Share a trade card (server-verified)"
                  className="p-1.5 rounded-lg text-gray-500 hover:text-emerald-400 hover:bg-dark-700 transition-all flex-shrink-0">
                  <TrendingUp className="w-5 h-5" />
                </button>
                <VoiceRecorderButton roomId={activeRoom} disabled={sending}
                  onSent={() => { loadMessages(); setToast('Voice note sent'); setTimeout(() => setToast(null), 2000); }}
                  onError={(msg) => { setError(msg); setTimeout(() => setError(null), 4000); }} />
                <input
                  type="text"
                  value={input}
                  onChange={e => {
                    const val = e.target.value;
                    setInput(val);
                    // Typing indicator: at most one ping per 5s (scales to busy rooms)
                    if (val.length > 0 && Date.now() - lastTypingSentRef.current > 5000) {
                      lastTypingSentRef.current = Date.now();
                      chatTyping(activeRoom).catch(() => {});
                    }
                    // Detect @mention typing
                    const match = val.match(/@(\w*)$/);
                    if (match) {
                      setMentionQuery(match[1]);
                      setShowMentions(true);
                    } else {
                      setShowMentions(false);
                      setMentionQuery('');
                    }
                  }}
                  ref={inputRef}
                  onKeyDown={e => e.key === 'Enter' && handleSend()}
                  placeholder={pendingImage ? 'Add a caption (optional)...' : `Message #${activeRoomInfo.name}...`}
                  disabled={sending}
                  maxLength={1000}
                  className="flex-1 bg-transparent text-sm text-white placeholder-gray-600 outline-none disabled:opacity-50"
                />
                <span className={`text-[10px] flex-shrink-0 ${input.length > 900 ? 'text-red-400' : 'text-gray-700'}`}>
                  {input.length > 0 ? `${input.length}/1000` : ''}
                </span>
                <button onClick={handleSend} disabled={(!input.trim() && !pendingImage) || sending}
                  className="p-2 rounded-xl bg-accent-primary text-dark-900 hover:bg-accent-primary/90 disabled:opacity-30 transition-all flex-shrink-0">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
            </>
          ) : isPaidLocked ? (
            <button onClick={() => setShowSubscribe(true)}
              className="w-full flex items-center justify-center gap-2 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 rounded-2xl px-4 py-3 transition-all">
              <Lock className="w-4 h-4 text-amber-400 flex-shrink-0" />
              <p className="text-sm text-amber-400 font-medium">Subscribe to post in #{activeRoomInfo.name}</p>
            </button>
          ) : (
            <div className="flex items-center gap-2 bg-dark-800/50 border border-dark-700/30 rounded-2xl px-4 py-3">
              <Lock className="w-4 h-4 text-gray-600 flex-shrink-0" />
              <p className="text-sm text-gray-500">Only admins can post in #{activeRoomInfo.name}</p>
            </div>
          )}
        </div>
      </div>

      {/* Right panel — online users */}
      <OnlinePanel
        users={onlineUsers}
        myRole={myRole}
        onModAction={handleModAction}
        onAssignRole={handleAssignRole}
      />

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl shadow-2xl text-sm font-medium bg-dark-700 text-white border border-dark-600 animate-[slideUp_0.3s_ease]">
          <style>{`@keyframes slideUp { from { transform: translateY(100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
          {toast}
        </div>
      )}

      {/* Create Channel Modal */}
      {showCreateModal && (
        <CreateChannelModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateChannel}
        />
      )}

      {guidelinesRequired && (
        <GuidelinesModal required onAcknowledge={() => setGuidelinesRequired(false)} />
      )}
      {showGuidelines && !guidelinesRequired && <GuidelinesModal onClose={() => setShowGuidelines(false)} />}
      {showBannedUsers && (
        <BannedUsersModal
          onClose={() => setShowBannedUsers(false)}
          onUnban={async (userId) => {
            try {
              await moderateChat('unban', { targetUserId: userId });
              setToast('User unbanned');
              setTimeout(() => setToast(null), 3000);
            } catch { setToast('Failed to unban'); setTimeout(() => setToast(null), 3000); }
          }}
        />
      )}
      {/* Tip Modal */}
      {tipTarget && (
        <TipModal target={tipTarget} onClose={() => setTipTarget(null)} />
      )}
      {lockedRoom && <LockedRoomModal room={lockedRoom} onClose={() => setLockedRoom(null)} />}
      {showPollModal && <CreatePollModal roomId={activeRoom} onClose={() => setShowPollModal(false)} onCreated={() => getRoomPolls(activeRoom).then(d => setRoomPolls(d?.items || [])).catch(() => {})} />}
      {activeAlert && <ModerationAlertModal alert={activeAlert} onAcknowledge={handleAcknowledgeAlert} />}
      {showMonetize && paidChannelStatus.enabled && (
        <MonetizeChannelModal
          channelId={activeRoom}
          channelName={activeRoomInfo.name}
          onClose={() => setShowMonetize(false)}
          onSubmitted={() => { loadRooms(); refreshMyApplications(); }}
        />
      )}
      {showSubscribe && paidChannelStatus.enabled && activeRoomInfo.isPaid && (
        <SubscribeChannelModal
          room={activeRoomInfo}
          onClose={() => setShowSubscribe(false)}
          onSubscribed={() => { loadRooms(); }}
        />
      )}
      {showShareTrade && (
        <ShareTradePickerModal
          roomId={activeRoom}
          onClose={() => setShowShareTrade(false)}
          onShared={() => { loadMessages(); setToast('Trade shared'); setTimeout(() => setToast(null), 2500); }}
        />
      )}
    </div>
  );
}
